import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
    _getWidgetAnimStagger,
    _entryAnimStarted,
    _entryAnimEnded,
    _whenNoEntryAnimations,
} from "./entryAnimCoordinator";

import TicketCard, { EMPTY_CARD_STATE } from "./TicketCard";

const areSameCards = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        const p = a[i]; const n = b[i];
        if (
            p.id !== n.id || p.title !== n.title || p.description !== n.description ||
            p.owner !== n.owner || p.percent !== n.percent || p.topLeftText !== n.topLeftText ||
            p.imageUrl !== n.imageUrl || p.openDate !== n.openDate || p.promiseDate !== n.promiseDate ||
            p.status !== n.status || p.headerText !== n.headerText ||
            p.completedDate !== n.completedDate || p.targetHours !== n.targetHours ||
            p.actualHours !== n.actualHours || p.description1 !== n.description1
        ) return false;
    }
    return true;
};


export default function WidgetCards({
    cardsData = [],
    minOpenTicketCount,
    autoRevertDurationinSec = 0,
    isDarkTheme = false,
    OpenTicketPlacement
}) {
    const parentRef = useRef(null);
    const gridRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const cardRefs = useRef([]);
    const autoRevertTimerRef = useRef(null);
    const resizeDebounceRef = useRef(null);
    const lastCalcTimeRef = useRef(0);
    const initialStateRef = useRef(null);
    const isFirstCalculationRef = useRef(true);
    const calcRetryRef = useRef(0);
    const calcHandlerRef = useRef(null);
    const calcRunIdRef = useRef(0);
    const dataVersionRef = useRef(0);
    const isCalculatingRef = useRef(false);
    const appliedMeasureTokenRef = useRef(-1);
    const layoutMeasureTokenRef = useRef(0);
    const noAnimationCalcRef = useRef(false);
    const step2ExpansionRef = useRef(false);
    const preInitStep2ExpansionRef = useRef(false);
    const lastCalcContainerHRef = useRef(0);
    // Set by the resize/zoom debounce when it changes maxTicketsToRender, so the
    // full-reset useLayoutEffect that the count change triggers knows the reset was
    // zoom-driven and must stay SILENT (no entry-animation replay, no blank) rather
    // than treating it as a fresh initial load. Cleared as soon as the full-reset
    // consumes it. Without this, every zoom step that changes the card count blanks
    // the widget and replays the 3-stage entry animation (and, at high zoom, routes
    // overflow through the animated expand-replay → the visible "redraw twice").
    const resizeFullResetRef = useRef(false);
    // True once the widget has completed its one-time entry load. Used to keep every
    // subsequent re-init (zoom re-estimate via the maxTickets clamp effect, resize)
    // silent so the entry animation never replays after the first load.
    const hasEverLoadedRef = useRef(false);
    // Dedup key for resize/zoom recalcs: `${dpr}x${width}x${height}`. Prevents a
    // second redundant recalc (visible as a double redraw) when the same zoom
    // change is observed more than once — e.g. the ResizeObserver firing again
    // after our own recalc resets hasInitiallyLoadedRef and re-runs the layout.
    const lastRecalcKeyRef = useRef("");
    const prevDisplayCardsRef = useRef([]);
    const prevLayoutDepsRef = useRef({ minOpenTicketCount: null, isOpenTicketPlacementTop: null });
    const normalizedCardsData = useMemo(() => (Array.isArray(cardsData) ? cardsData : []), [cardsData]);
    // Short label for debug logs — first card title (≤20 chars) so two identical-data
    // widgets log a shared tag and you can confirm they diverge at the same line.
    const widgetDbgId = useMemo(
        () => {
            const title = normalizedCardsData[0]?.title || normalizedCardsData[0]?.id || "?";
            return String(title).slice(0, 20);
        },
        // Only recalculate when the first card changes — intentionally not re-firing on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [normalizedCardsData[0]?.title, normalizedCardsData[0]?.id]
    );
    // Live ref so closures created at mount (empty-deps effects) log the current
    // widget id instead of the stale "?" captured before data arrived.
    const widgetDbgIdRef = useRef(widgetDbgId);
    widgetDbgIdRef.current = widgetDbgId;
    const [widgetViewportHeight, setWidgetViewportHeight] = useState(0);
    const [widgetWidth, setWidgetWidth] = useState(0);
    // DOM-measured element dimensions — stored as a ref (not state) so updates
    // from inside RAF callbacks don't trigger unintended re-renders.
    // The estimator reads this ref directly; it reflects correct values starting from
    // the second calc (after the first DOM measurement is available).
    const measuredDimsRef = useRef(null);
    // Expose as a stable object so the estimator can read it via closure.
    // We use a single-element array trick so the useMemo closure always reads the
    // latest ref without needing it in its dependency list.
    const measuredDims = measuredDimsRef.current;
    const headerLabelModesRef = useRef({});
    const headerSpanRefs = useRef({});
    const [headerLabelVersion, setHeaderLabelVersion] = useState(0);
    const prevHeaderDataKeyRef = useRef(null);
    const defaultHeightOfOpenCard = 175;
    // Pure aspect-ratio height: card body height = width × ratio, no floor or ceiling.
    // This ensures a constant visual aspect ratio across all zoom levels and widget sizes.
    const OPEN_CARD_ASPECT_RATIO = 0.58;
    // Step the open-card body height to 8-px increments so minor width differences between
    // two grid columns (e.g. 360 px vs 368 px due to CSS sub-pixel layout) never produce
    // different dynamicOpenCardHeight values.
    const OPEN_CARD_HEIGHT_STEP_PX = 8;
    const dynamicOpenCardHeight = useMemo(() => {
        if (widgetWidth <= 0) return defaultHeightOfOpenCard;
        const computed = Math.round(widgetWidth * OPEN_CARD_ASPECT_RATIO);
        // Step to nearest 8-px increment for layout stability across sibling columns.
        const stepped = Math.max(OPEN_CARD_HEIGHT_STEP_PX, Math.floor(computed / OPEN_CARD_HEIGHT_STEP_PX) * OPEN_CARD_HEIGHT_STEP_PX);

        // ── Clamp to the available viewport height ───────────────────────────
        // The aspect-ratio formula (width × 0.58) is width-driven only — it has
        // no awareness of the container's actual height. At low zoom levels the
        // grid can reflow into wide-but-short cells, so width × 0.58 can exceed
        // the vertical space the widget actually has. Because at least
        // safeMinOpenCount open card is ALWAYS rendered (see estimatedTicketsToRender /
        // calculateAndAdjustHeight), an unclamped height here forces an open card
        // taller than the container — its header, body, border, gap and footer
        // (Opened/Promised/Completed meta row) then overflow past the widget's
        // bottom edge, clipped only by the scroll container's overflow:hidden.
        //
        // Cap the body height so a single open card's TOTAL rendered height
        // (normal header block + card border + card gap + body) can never exceed
        // the available viewport, leaving the same containerPadPx (8px) margin
        // the layout budget reserves everywhere else.
        if (widgetViewportHeight > 0) {
            const containerPadPx = 8;
            // Chrome reserve = normal header block + outer card border + card gap.
            //
            // IMPORTANT: do not assume the textbook constants (28px header / 2px
            // border). At extreme zoom the *actual* rendered border can be far
            // larger than the nominal 1px CSS value — e.g. the diagnostic log at
            // dpr≈0.313 showed normalH=32.4px and border=6.4px (vs. the textbook
            // 28px / 2px), and a clamp using the textbook numbers under-reserved
            // by ~6px per side, leaving the open card ~75px too tall and still
            // overflowing the bottom edge.
            //
            // measuredDimsRef carries the most recent DOM-measured values from
            // calculateAndAdjustHeight (normalHeaderH, cardDivBorderTotal, gap),
            // which reflect the CURRENT zoom once any calc has run. They may lag
            // by one resize step right after a zoom change, but that is still far
            // closer to reality than fixed constants — and on the immediately
            // following silent recalc the measured values (and this clamp) update
            // to match, self-correcting any residual slack.
            const measured = measuredDimsRef.current;
            const reserveGapPx = isDarkTheme ? 5 : 3;
            const reserveHeaderPx = (measured && Number.isFinite(measured.normalHeaderH)) ? measured.normalHeaderH : 28;
            const reserveBorderPx = (measured && Number.isFinite(measured.cardDivBorderTotal)) ? measured.cardDivBorderTotal : 2;
            const reserveGapMeasuredPx = (measured && Number.isFinite(measured.gap)) ? measured.gap : reserveGapPx;
            const singleCardChromeReservePx = reserveHeaderPx + reserveBorderPx + reserveGapMeasuredPx;
            const maxBodyHeight = widgetViewportHeight - (2 * containerPadPx) - singleCardChromeReservePx;
            const cappedMax = Math.max(
                OPEN_CARD_HEIGHT_STEP_PX,
                Math.floor(maxBodyHeight / OPEN_CARD_HEIGHT_STEP_PX) * OPEN_CARD_HEIGHT_STEP_PX
            );
            return Math.min(stepped, cappedMax);
        }

        return stepped;
    }, [widgetWidth, widgetViewportHeight, isDarkTheme]);
    const defaultHeightOfHeaderPx = 21;
    const defaultHeightOfAfter3HeaderPx = 25;
    // Card gap in dark theme is 5px, light theme is 3px (see cardGapPx in JSX).
    // Using 5 (the maximum) keeps the budget conservative on dark theme and
    // prevents at-rest overflow.  Light theme over-reserves by 2px/card — acceptable.
    const defaultEstimatedCardSpacingPx = 5;

    const MIN_MEDIUM_HEADERS = 3;

    const [isToggling, setIsToggling] = useState(null);
    const hasUserInteractedRef = useRef(false);
    const isEntryAnimatingRef = useRef(false);   // true while initial load animation is playing
    const entryAnimCountedRef = useRef(false);   // dedup guard for the global animation counter
    const [entryAnimStage, setEntryAnimStage] = useState(0); // 0=idle, 1=stack slide-in, 2=headers open, 3=bodies open
    const entryAnimTimersRef = useRef([]);
    const [scrollEnabled, setScrollEnabled] = useState(false);

    const isOpenTicketPlacementTop = useMemo(
        () => String(OpenTicketPlacement || "bottom").toLowerCase() === "top",
        [OpenTicketPlacement]
    );
    // ── minOpenTicketCount semantics (0-99) ──────────────────────────────
    //   0      → render NO open cards at all (collapsed-only; nothing expanded).
    //   1-98   → render up to that many minimum open cards (clamped/floored
    //            into [1,98]).
    //   99     → "no maximum" — render as many open cards as fit the viewport
    //            (this is the legacy behavior previously triggered by 0).
    // Non-finite input falls back to 1 (legacy default "open at least one").
    const safeMinOpenCountForRender = useMemo(() => {
        const n = Number(minOpenTicketCount);
        if (!Number.isFinite(n)) return 1;
        const floored = Math.floor(n);
        if (floored <= 0) return 0;
        if (floored >= 99) return 99;
        return Math.max(1, Math.min(98, floored));
    }, [minOpenTicketCount]);


    const estimatedTicketsToRender = useMemo(() => {
        const totalTickets = normalizedCardsData.length;
        if (totalTickets === 0) return 0;

        const minOpen = safeMinOpenCountForRender === 99
            ? totalTickets
            : Math.min(safeMinOpenCountForRender, totalTickets);
        const containerPadPx = 8;
        const effectiveHeight = Math.max(0, widgetViewportHeight - (2 * containerPadPx));
        if (effectiveHeight <= 0) return minOpen;

        // Use the real inter-card gap (3 px light / 5 px dark) matching calculateAndAdjustHeight
        // and WidgetCardsLandscape.  The old defaultEstimatedCardSpacingPx=5 over-reserved
        // 2 px/card in light theme, reducing safeMaxCards by 1 for every ~13 extra cards.
        const estimatedCardSpacingPx = isDarkTheme ? 5 : 3;
        // Full visual height of a medium/open header (content-box, matches JSX constants):
        //   normalHeaderMinHeightPx(22) + padding(2+2) + border(1+1) = 28 px
        // Must match normalHeaderBlockPxForCalc in calculateAndAdjustHeight.
        //
        // ── Font-driven correction (mirrors calculateAndAdjustHeight) ────────
        // The flat 28px assumes a 22px text content-box. But normalHeaderFontRem
        // (= 0.72 + 0.18 × widthScale, widthScale UNCLAMPED above 371px width)
        // can make the rendered header content far taller than 22px at large
        // widget widths / extreme zoom (e.g. ~46px observed at widgetW≈1520px,
        // vs the 26px the unclamped estimate assumed — a ~20px/header gap).
        // Without this correction the estimator over-counts how many open cards
        // fit, Step 2 then clamps total card count to that wrong estimate
        // (`safeByEstimate`), and the layout ends up under-filled — leaving large
        // unused space and far fewer medium/collapsed headers visible than the
        // remaining space could actually hold.
        const _estimRootFontPx = (typeof window !== "undefined" && window.getComputedStyle)
            ? (parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16)
            : 16;
        const _estimWidthScale = Math.max(0, (widgetWidth - 150) / (371 - 150));
        const _estimNormalHeaderFontRem = Number((0.72 + 0.18 * _estimWidthScale).toFixed(2));
        const _estimFontDrivenContentPx = Math.ceil(_estimNormalHeaderFontRem * _estimRootFontPx * 1.2);
        const _estimHeaderContentPx = Math.max(22, _estimFontDrivenContentPx);
        const estimateHeaderBlockPx = _estimHeaderContentPx + 4 /* padding */ + 2 /* border, light estimate */;
        // index.css forces content-box globally; the JSX adjusts flex-basis to account for
        // body padding so that the visual height equals dynamicOpenCardHeight exactly.
        // Collapsed card height: use the DPR-corrected formula.
        // We deliberately do NOT use measuredDimsRef here because those measurements
        // are cached from the last successful calc — if the user changed zoom level,
        // the cached value is from the OLD zoom level and would give wrong estimates.
        // The DPR formula is always current (window.devicePixelRatio updates with zoom).
        // calculateAndAdjustHeight always uses fresh getComputedStyle measurements, so any
        // estimator inaccuracy is corrected by Step 2 or trim on the first calc.
        const _estimDpr = window.devicePixelRatio || 1;
        // getComputedStyle.borderTopWidth always returns the CSS value (1px) regardless
        // of DPR — it is NOT sub-pixel corrected.  Math.round(DPR)/DPR breaks at
        // sub-integer DPR (e.g. 33% zoom → DPR≈0.33 → Math.round(0.33)=0 → border=0)
        // causing the estimator to use 3px collapsed height while the DOM uses 5px,
        // making estimatedTicketsToRender return 400+ cards when only ~180 actually fit.
        // Fix: clamp the border to a minimum of 1px (matching the CSS value).
        const _estimBorder = Math.max(1, Math.round(_estimDpr) / _estimDpr);
        // Card div border is DPR-corrected (1px CSS × DPR rounding = _estimBorder per side).
        // Matches fitCheckBodyPadOverhead = mediumCardBorderPxForCalc in calculateAndAdjustHeight.
        const openBodyPadOverheadPx = 2 * _estimBorder; // card div border (DPR-corrected)
        const estimatedOpenBodyPerTicketPx = dynamicOpenCardHeight + openBodyPadOverheadPx;
        // ── Collapsed-card height: prefer the last DOM-measured value ───────────
        // The DPR-formula (3 + 2×Math.max(1, Math.round(dpr)/dpr)) assumes the
        // rendered collapsed header height scales linearly with the CSS border —
        // but real measurements (BCR) show it can be 4-6× larger at sub-1 DPR
        // (e.g. formula≈5-6px while BCR/DOM≈24-40px — observed across the
        // 107%-320% zoom matrix). That ~5x error makes the estimator wildly
        // over-count how many cards fit, producing a wrong `result` that the
        // full-reset path then "corrects" — only for the NEXT estimator run
        // (using the same broken formula) to predict differently again, so
        // resize/zoom oscillates between mismatched card counts (visible as
        // 31→3→31→23→31… full-reset thrashing) instead of converging like a
        // fresh page-load (which settles after its first real DOM measurement).
        // Using the most recent real measurement — even if captured at a
        // slightly different zoom level — is far closer to truth than a
        // formula that's off by 5x, and stabilizes the estimate so it agrees
        // with the actual fit-check (preventing the thrash / "stuck" symptom).
        const _measuredCollapsedH = measuredDimsRef.current && Number.isFinite(measuredDimsRef.current.collapsedHeaderH)
            ? measuredDimsRef.current.collapsedHeaderH
            : null;
        const collapsedCardBaselinePx = (_measuredCollapsedH != null && _measuredCollapsedH > 0)
            ? Math.max(3, _measuredCollapsedH)
            : Math.max(3, 3 + 2 * _estimBorder);
        const mediumCardBorderPx = 2 * _estimBorder;
        const _estimGap = isDarkTheme ? 5 : 3;
        const _estimNormalHeaderH = 22 + 4 + 2 * _estimBorder; // minH + pad + border
        // Medium upgrade cost above collapsed baseline.
        const mediumHeaderBlockPx = _estimNormalHeaderH + _estimGap;
        const mediumUpgradeAboveBaseline = Math.max(0, mediumHeaderBlockPx + mediumCardBorderPx - collapsedCardBaselinePx);

        // Priority-first: open tickets claim space first.
        // When safeMinOpenCountForRender === 99 (no max), we find the maximum open count
        // that fits by checking open-card cost only — the remaining space fills with as
        // many collapsed cards as possible (we don't require all totalTickets to be visible).
        // When a specific min is set, we check open + ALL remaining collapsed as a pessimistic
        // bound, BUT we never reduce fittedOpenCount below safeMinOpenCountForRender.
        // Reason: at extreme zoom-out (33%), totalTickets×5px alone can exceed availableHeight,
        // making the check always fail and reducing fittedOpenCount to 0 → returning 1 card.
        // With only 1 card in displayCardsData, targetOpenCount = min(minOpen, 1) = 1, so
        // only 1 open card renders even when the container has room for 2.
        // The fix: for regular mode, stop the while-loop at safeMinOpenCountForRender.
        // The actual calc will trim collapsed cards to make room — we don't need the
        // estimator to guarantee all totalTickets fit.
        //
        // safeMinOpenCountForRender === 0 means "no open cards at all" — skip the
        // open-card fitting entirely; only collapsed cards are estimated.
        const noOpenMode = safeMinOpenCountForRender === 0;
        const noMaxMode = safeMinOpenCountForRender === 99;

        if (noOpenMode) {
            const safeMaxCardsNoOpen = Math.floor(effectiveHeight / collapsedCardBaselinePx);
            return Math.max(1, Math.min(totalTickets, safeMaxCardsNoOpen));
        }

        const maxPossibleOpen = noMaxMode
            ? Math.min(totalTickets, Math.floor(effectiveHeight / Math.max(1, estimateHeaderBlockPx + estimatedOpenBodyPerTicketPx)))
            : minOpen;
        let fittedOpenCount = maxPossibleOpen;
        const openCountFloor = noMaxMode ? 0 : safeMinOpenCountForRender;
        while (fittedOpenCount > openCountFloor) {
            const openTicketsPx = fittedOpenCount * (estimateHeaderBlockPx + estimatedOpenBodyPerTicketPx);
            const openSpacingPx = Math.max(0, fittedOpenCount - 1) * estimatedCardSpacingPx;
            const nonOpenBaseline = noMaxMode ? 0 : (totalTickets - fittedOpenCount) * collapsedCardBaselinePx;
            if ((openTicketsPx + openSpacingPx + nonOpenBaseline) <= effectiveHeight) break;
            fittedOpenCount -= 1;
        }

        if (fittedOpenCount === 0) return 1;

        // Total capacity: open cards + collapsed cards, reserving space for MIN_MEDIUM_HEADERS
        // medium upgrades so applyCompressedModes has budget to promote some collapsed → medium.
        // Without this reserve, all remaining space after open cards goes to collapsed cards,
        // leaving availableBodyHeight ≈ 0 after open card placement → 0 medium headers.
        const openFitCostPx = fittedOpenCount * (estimateHeaderBlockPx + estimatedOpenBodyPerTicketPx)
            + Math.max(0, fittedOpenCount - 1) * estimatedCardSpacingPx;
        const _medUpgradePxEst = Math.max(1, _estimNormalHeaderH + _estimGap + mediumCardBorderPx - collapsedCardBaselinePx);
        const _medReserveEst = Math.min(3, Math.max(0, totalTickets - fittedOpenCount)) * _medUpgradePxEst;
        const remainingForCollapsed = Math.max(0, effectiveHeight - openFitCostPx - _medReserveEst);
        const safeMaxCards = fittedOpenCount + Math.floor(remainingForCollapsed / collapsedCardBaselinePx);
        const result = Math.min(totalTickets, Math.max(fittedOpenCount, safeMaxCards));
        const _estDpr = window.devicePixelRatio || 1;

        return result;
        // measuredDimsRef is a ref — intentionally NOT in deps.  The estimator reads the
        // ref's current value on every run; it will use DOM measurements starting from the
        // second natural re-run (triggered by data/settings changes after first layout calc).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [normalizedCardsData.length, safeMinOpenCountForRender, widgetViewportHeight, widgetWidth, dynamicOpenCardHeight, isDarkTheme, widgetDbgId]);

    // Removed: per-render estimator noise log (was firing on every render).

    const hasInitiallyLoadedRef = useRef(false);
    const [maxTicketsToRender, setMaxTicketsToRender] = useState(estimatedTicketsToRender);

    // ── Apply the estimator's guess ONLY before the first real layout calc ──
    // Root cause of resize/zoom "thrashing" (inconsistent vs. page-refresh
    // rendering, e.g. repeated full-resets like 24→31→24→…): collapsed-header
    // height is not a pure function of DPR — it also depends on whether the
    // header TEXT WRAPS, which depends on widget WIDTH. So a measurement
    // cached from one width/zoom tick (e.g. collapsedH≈5px, single line) can
    // be wildly wrong for the next tick at a different width (e.g. text wraps
    // → collapsedH≈25-40px). The estimator then guesses a too-large card count
    // (e.g. 31), this effect immediately commits it to maxTicketsToRender,
    // `cardsChanged` fires, and a full-reset replays — only for the REAL
    // fit-check (DOM-measured, authoritative) to trim it back down on the next
    // tick, repeating the cycle. A page refresh never thrashes because it only
    // ever takes ONE estimate (for the first paint) and then defers entirely
    // to the real, DOM-measured fit-check / Step-2 / trim logic thereafter.
    //
    // Fix: mirror that — use the estimate solely to size the FIRST render
    // (before any real measurement exists). Once `calculateAndAdjustHeight`
    // has completed its first pass (hasInitiallyLoadedRef=true), leave
    // maxTicketsToRender entirely to the authoritative DOM-measured fit-check
    // / Step-2 "show more" / trim paths — never let a re-estimate (which can
    // be stale/wrong for the new dimensions) force a full-reset mid-resize.
    useEffect(() => {
        if (hasInitiallyLoadedRef.current) return;
        setMaxTicketsToRender((prev) => {
            const boundedEstimate = Math.max(
                safeMinOpenCountForRender,
                Math.min(normalizedCardsData.length, estimatedTicketsToRender)
            );
            // On a zoom/resize-driven re-init (widget already loaded once), never SHRINK
            // the count — only grow it. Same reason as the resize-reset path: the calc
            // keeps all cards visible, so shrinking to an under-counting estimate just
            // flickers the ticket count down and back up mid-resize.
            const target = hasEverLoadedRef.current
                ? Math.max(prev, boundedEstimate)
                : boundedEstimate;
            if (target === prev) return prev;
            // If the widget already loaded once, this clamp-effect firing is a
            // zoom/resize-driven re-init (the resize-reset cleared hasInitiallyLoadedRef,
            // re-activating this effect) — mark the resulting full-reset silent so it
            // doesn't replay the entry animation.
            if (hasEverLoadedRef.current) {
                resizeFullResetRef.current = true;
            }

            return target;
        });
    }, [normalizedCardsData.length, safeMinOpenCountForRender, estimatedTicketsToRender, widgetDbgId]);

    // Keep refs to the latest estimator and maxTickets values so the resize debounce
    // can read them without a stale closure.  Updated on every render.
    const estimatedTicketsToRenderRef = useRef(estimatedTicketsToRender);
    estimatedTicketsToRenderRef.current = estimatedTicketsToRender;
    const maxTicketsToRenderRef = useRef(maxTicketsToRender);
    maxTicketsToRenderRef.current = maxTicketsToRender;

    const displayCardsData = useMemo(
        () => {
            return normalizedCardsData.slice(0, maxTicketsToRender);
        },
        [normalizedCardsData, maxTicketsToRender]
    );

    const themeStyles = useMemo(() => ({
        cardBackground: isDarkTheme ? "rgb(52, 52, 52)" : "#ffffff",
        cardBorder: isDarkTheme ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.12)",
        cardBoxShadow: isDarkTheme ? "0 2px 8px rgba(0,0,0,0.60)" : "none",
        bodyTextColor: isDarkTheme ? "#d0d0d0" : "inherit",
        mutedTextColor: isDarkTheme ? "#a8a8a8" : "#6c757d",
    }), [isDarkTheme]);

    const [cardStates, setCardStates] = useState(
        displayCardsData.map((_, index) => {
            // When safeMinOpenCountForRender === 0, NO card should be auto-expanded —
            // every card renders collapsed (no open/expanded cards at all).
            const shouldAutoOpenFirst = safeMinOpenCountForRender !== 0 && index === 0;
            return {
                isExpanded: shouldAutoOpenFirst,
                isHalfOpen: false,
                fullHeight: 0,
                visibleBodyHeight: 0,
                ExtraMargin: 0,
                renderMode: shouldAutoOpenFirst ? "open" : "collapsed",
                priorCompressedMode: "collapsed",
                lastCollapsedMode: false
            };
        })
    );
    const [isReady, setIsReady] = useState(false);
    const [disableBodyTransition, setDisableBodyTransition] = useState(true);
    const [disableHeaderTransition, setDisableHeaderTransition] = useState(true);

    // Keep open cards' body height in lock-step with the live dynamicOpenCardHeight during
    // resize/zoom, WITHOUT waiting for the ~200ms-debounced recalc. dynamicOpenCardHeight
    // updates synchronously when widgetWidth/widgetViewportHeight change, but an open card's
    // rendered height comes from cardState.visibleBodyHeight — which only the debounced calc
    // refreshes. In the gap, the open ticket renders at its stale, pre-zoom height (the
    // inconsistent open-card height seen mid-resize). Patch open cards here immediately so
    // they scale with the widget; the debounced calc still finalizes open/medium/collapsed
    // counts and overflow. Gated on hasEverLoadedRef so it never interferes with the
    // one-time entry animation (which drives heights itself), and it's a no-op unless an
    // open card's height actually differs (so it won't fight user toggles — those don't
    // change dynamicOpenCardHeight).
    useLayoutEffect(() => {
        if (!hasEverLoadedRef.current) return;
        const h = Math.floor(dynamicOpenCardHeight);
        if (!(h > 0)) return;
        setCardStates((prev) => {
            let changed = false;
            const next = prev.map((s) => {
                if (s.renderMode === "open" && s.visibleBodyHeight !== h) {
                    changed = true;
                    return { ...s, fullHeight: h, visibleBodyHeight: h };
                }
                return s;
            });
            return changed ? next : prev;
        });
    }, [dynamicOpenCardHeight]);
    const isRevertAnimatingRef = useRef(false);
    const revertAnimationTimerRef = useRef(null);
    const toggleCardLatestRef = useRef(null);
    const onToggleCard = useCallback((i) => toggleCardLatestRef.current?.(i), []);
    // True while a user-toggle open/close transition (900ms flex-basis) is playing.
    // measureAndApply skips its forced-layout pass during this window.
    const isToggleAnimatingRef = useRef(false);
    const toggleAnimTimerRef = useRef(null);
    const isMeasuringRef = useRef(false);
    const [imageErrors, setImageErrors] = useState({});
    const [compactStatusStyle, setCompactStatusStyle] = useState({
        headerPadPx: 2,
        headerMinHeightPx: 10,
        statusPadPx: 2,
        headerFontRem: 0.75,
        normalHeaderFontRem: 0.90,
        statusLabelRem: 0.8,
        statusValueRem: 1,
        bodyPaddingPx: 6,
        avatarSizePx: 41,
        avatarFontPx: 15,
        ownerFontRem: 0.78,
        topLeftFontRem: 0.90,
        titleFontRem: 1.30,
        descFontRem: 0.83,
        metaFontRem: 0.70,
        titleLines: 1,
        descLines: 1,
        containerPadPx: 3,
        avatarWrapWidthPx: 67,
        showHeaderText: true,
        headerLeftPadPx: 8,
        bodyRowGapPx: 4,
        centerRowPadPx: 2,
        centerRowMinHeightPx: 52,
        titleMarginBottomRem: 0.15,
        descMarginBottomRem: 0.40,
        metaRowPadPx: 4,
    });

    useLayoutEffect(() => {
        if (!parentRef.current) return;

        const updateViewportHeight = () => {
            const rawHeight = scrollContainerRef.current?.clientHeight || parentRef.current?.clientHeight || 0;
            const rawWidth = parentRef.current?.clientWidth || 0;
            // Quantize height to the nearest 4 px (floor).
            // Purpose: CSS sub-pixel rounding causes same-row grid cells to measure up to
            // 1 px apart; 4 px quantization absorbs that without over-reserving.
            // We deliberately do NOT use 8 px here: safeMaxCards changes every 5 px of
            // effectiveHeight, so an 8 px floor can silently hide 1–2 cards that would
            // physically fit (8 px of over-reservation vs. a 5 px card boundary).
            // 4 px is conservative enough to normalise CSS rounding (≤ 1 px) while only
            // over-reserving 0–3 px — at most floor(3/5) = 0 extra hidden cards.
            const quantizedHeight = Math.max(0, Math.floor(rawHeight / 4) * 4);
            // Floor width to the nearest 8px so dynamicOpenCardHeight is never inflated.
            const quantizedWidth = Math.max(0, Math.floor(rawWidth / 8) * 8);
            // Removed: dims log (redundant with layout-input log).
            setWidgetViewportHeight((prev) => (prev === quantizedHeight ? prev : quantizedHeight));
            setWidgetWidth((prev) => (prev === quantizedWidth ? prev : quantizedWidth));


            // When the widget container resizes (e.g. panel open/close, grid reflow),
            // schedule a silent recalculation so card heights stay correct without
            // replaying the entry animation.
            //
            // NOTE: hasInitiallyLoadedRef.current guard is intentionally omitted here.
            // The ResizeObserver fires as soon as the container gets its real dimensions —
            // which can happen BEFORE the initial calculateAndAdjustHeight completes
            // (hasInitiallyLoadedRef.current is still false).  If we skip the debounce
            // in that window, the initial calc runs with widgetWidth=0 / dynamicOpenCardHeight=175px
            // and the correction is never scheduled, leaving two identical widgets with
            // different body heights depending on render timing.
            //
            // By always scheduling the 200ms debounce, calcHandlerRef.current (which is
            // updated after every render) will point to the closure that carries the real
            // widgetWidth and dynamicOpenCardHeight by the time the timeout fires.
            // Always schedule the silent resize debounce, even if the entry animation is
            // currently playing.  Previously the guard `!isEntryAnimatingRef.current`
            // caused zoom changes during the animation to be silently dropped — the widget
            // would never receive the updated dimensions until the animation finished
            // (sometimes 3–8 seconds), producing the "doesn't update on frequent zoom changes"
            // symptom.  The silent calc uses noAnimationCalcRef=true which automatically
            // cancels any running animation via calcRunIdRef and applies the correct layout.
            if (quantizedHeight > 0) {
                if (resizeDebounceRef.current) clearTimeout(resizeDebounceRef.current);
                resizeDebounceRef.current = setTimeout(() => {
                    // If the entry animation is already playing and the container height
                    // has not changed since the last calc, this debounce was triggered by
                    // the initial-mount ResizeObserver fire — skip it so the animation is
                    // not cancelled prematurely. Real zoom/resize changes will have a
                    // different height and will still proceed normally.
                    if (isEntryAnimatingRef.current && hasInitiallyLoadedRef.current) {
                        const rawH = scrollContainerRef.current?.clientHeight || parentRef.current?.clientHeight || 0;
                        const curH = Math.max(0, Math.floor(rawH / 4) * 4);
                        if (curH === lastCalcContainerHRef.current) {
                            resizeDebounceRef.current = null;
                            return;
                        }
                    }
                    // On resize/zoom: re-run the same complete initialisation as a page
                    // refresh.  The estimator already re-ran for the new dimensions
                    // (triggered by setWidgetViewportHeight / setWidgetWidth above).
                    // Reading its latest value from the ref and resetting maxTicketsToRender
                    // causes displayCardsData to update → useLayoutEffect full-reset →
                    // calc fires with the correct card count for the new zoom level,
                    // identical to page refresh.  If the estimate matches the current
                    // count, fall back to a plain silent recalc so dimensions still apply.
                    // Use the estimator ref directly — it already clamps to
                    // [safeMinOpen, totalCards].  Do NOT reference safeMinOpenCountForRender
                    // or normalizedCardsData.length here: this closure was created at
                    // initial mount (empty deps) so those variables are stale (often 0/1),
                    // which would corrupt the estimate (e.g. 5→1 instead of 5→5).
                    // Skip if a recalc already ran for this exact (dpr, w, h) — prevents
                    // the same zoom change from redrawing twice.
                    const _recalcKey = `${(window.devicePixelRatio || 1).toFixed(3)}x${parentRef.current?.clientWidth || 0}x${parentRef.current?.clientHeight || 0}`;
                    if (_recalcKey === lastRecalcKeyRef.current) {
                        resizeDebounceRef.current = null;
                        return;
                    }
                    lastRecalcKeyRef.current = _recalcKey;
                    const newEstimate = estimatedTicketsToRenderRef.current;
                    const prevMax = maxTicketsToRenderRef.current;
                    // Never SHRINK the rendered count on a resize/zoom re-init. The
                    // authoritative DOM calc always keeps every card visible (it just
                    // re-distributes open/medium/collapsed), so dropping maxTicketsToRender
                    // to an under-counting estimate only causes a visible count
                    // drop-then-restore (the estimate renders fewer cards, then
                    // overflow-silent expands back to the full count) — the inconsistent
                    // ticket count seen mid-resize. Only grow when the estimate genuinely
                    // exceeds the current count.
                    const target = Math.max(prevMax, newEstimate);
                    // Reset hasInitiallyLoadedRef so the full-reset path treats this as
                    // the first real calc (suppresses DATA-CHANGE warnings).
                    hasInitiallyLoadedRef.current = false;
                    noAnimationCalcRef.current = true;
                    isFirstCalculationRef.current = true;
                    if (target !== prevMax) {
                        // Count grew → displayCardsData changes → full-reset path →
                        // calc runs with the correct count for the new zoom level.

                        // Mark the full-reset this count change triggers as zoom-driven so
                        // it stays silent (no animation replay / blank) — see resizeFullResetRef.
                        resizeFullResetRef.current = true;
                        setMaxTicketsToRender(target);
                    } else {
                        // Count unchanged (or estimate would only shrink it) — just do a
                        // silent recalc for the new dimensions, keeping all current cards.
                        calcHandlerRef.current?.();
                    }
                    resizeDebounceRef.current = null;
                }, 200);
            }
        };

        updateViewportHeight();

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(() => updateViewportHeight());
            observer.observe(parentRef.current);
            return () => observer.disconnect();
        }

        window.addEventListener("resize", updateViewportHeight);
        return () => window.removeEventListener("resize", updateViewportHeight);
    }, []);

    useLayoutEffect(() => {
        const layoutDepsChanged =
            prevLayoutDepsRef.current.minOpenTicketCount !== minOpenTicketCount ||
            prevLayoutDepsRef.current.isOpenTicketPlacementTop !== isOpenTicketPlacementTop;
        const prevData = prevDisplayCardsRef.current;
        prevDisplayCardsRef.current = displayCardsData;
        prevLayoutDepsRef.current = { minOpenTicketCount, isOpenTicketPlacementTop };

        const cardsChanged = !areSameCards(prevData, displayCardsData);
        if (hasInitiallyLoadedRef.current && !layoutDepsChanged && !cardsChanged) {
            return;
        }

        // ── Step 2 resize expansion — skip full reset ─────────────────────────
        // When calculateAndAdjustHeight Step 2 adds more cards after initial load,
        // it sets step2ExpansionRef=true and returns early (without calling
        // setCardStates/setIsReady).  We detect the expansion here by checking that
        // the existing cards are unchanged and the new displayCardsData is simply
        // a longer slice of the same underlying data.
        //
        // We deliberately do NOT use !cardsChanged here because Step 2 always
        // changes displayCardsData.length (adding cards makes cardsChanged=true).
        // Instead we do a prefix-equality check: prevData must be an exact prefix
        // of the new displayCardsData (same IDs / content, just more cards appended).
        // This distinguishes a Step 2 expansion from a real data change where card
        // content also differs.
        //
        // layoutDepsChanged is still required to be false so that a concurrent
        // minOpenTicketCount change (co-batched with Step 2) falls through to
        // the full-reset that correctly handles the config change.
        const isPureStep2Expansion = (
            step2ExpansionRef.current &&
            hasInitiallyLoadedRef.current &&
            !layoutDepsChanged &&
            (
                !cardsChanged ||   // canAdd=0 edge-case: nothing actually changed
                (
                    prevData.length < displayCardsData.length &&
                    areSameCards(prevData, displayCardsData.slice(0, prevData.length))
                )
            )
        );
        if (isPureStep2Expansion) {
            step2ExpansionRef.current = false;
            cardRefs.current = new Array(displayCardsData.length);
            // Initialize collapsed states for the newly added cards so they render
            // correctly during the 2-frame gap before the silent calc applies layout.
            setCardStates(prev => {
                if (prev.length >= displayCardsData.length) return prev;
                const extra = displayCardsData.slice(prev.length).map(() => ({
                    isExpanded: false, isHalfOpen: false, fullHeight: 0,
                    visibleBodyHeight: 0, ExtraMargin: 0,
                    renderMode: "collapsed", priorCompressedMode: "collapsed",
                }));
                return [...prev, ...extra];
            });
            // noAnimationCalcRef and isFirstCalculationRef were already set by Step 2.
            // Schedule an immediate silent calc with the new card count.
            calcRunIdRef.current += 1;
            // Consume the current measure token so measureAndApply does not fire with
            // stale card states from the pre-expansion layout.  The upcoming silent
            // calc will increment layoutMeasureTokenRef and set up a fresh measurement.
            appliedMeasureTokenRef.current = layoutMeasureTokenRef.current;
            requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
            return;
        }

        // ── Pre-init Step 2 expansion — skip full reset ───────────────────────
        // When the estimator under-counts cards before initial load (DPR mismatch),
        // calculateAndAdjustHeight Step 2 sets preInitStep2ExpansionRef=true and
        // increases maxTicketsToRender.  We must NOT do a full reset here (which
        // would replay setIsReady(false) + the entry animation a second time).
        // Instead: expand card refs, reset card states for the new count (widget is
        // still hidden), and re-schedule the initial calc so the animation plays once.
        if (preInitStep2ExpansionRef.current && !hasInitiallyLoadedRef.current && !layoutDepsChanged) {
            preInitStep2ExpansionRef.current = false;
            cardRefs.current = new Array(displayCardsData.length);
            calcRunIdRef.current += 1;
            setCardStates(
                displayCardsData.map(() => ({
                    isExpanded: false,
                    isHalfOpen: false,
                    fullHeight: 0,
                    visibleBodyHeight: 0,
                    ExtraMargin: 0,
                    renderMode: "collapsed",
                    priorCompressedMode: "collapsed",
                }))
            );
            const doCalc = () => requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
            // Defer the replay calc until NO widget is playing its entry animation.
            // The replay is a heavy pass (DOM measurement + full-count re-render —
            // possibly hundreds of cards) and the widget is still hidden, so there
            // is no urgency.  Running it eagerly lands it mid-animation of PARALLEL
            // widgets and causes visible stutter.  The 6s max-wait inside
            // _whenNoEntryAnimations bounds the worst case.
            const runDeferred = () => _whenNoEntryAnimations(doCalc);
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(runDeferred).catch(runDeferred);
            } else {
                runDeferred();
            }
            return;
        }

        // ⚠ full-reset on resize = bug: maxTicketsToRender changed during resize
        if (hasInitiallyLoadedRef.current && !layoutDepsChanged && cardsChanged) {

        }

        // Was this reset triggered by a zoom/resize (vs. a genuine data/config
        // change)? If so, keep it silent: the resize debounce already set
        // noAnimationCalcRef=true, and we must NOT clear it here or blank the widget
        // below — otherwise the zoom replays the entry animation (and, at high zoom,
        // the animated expand-replay → the visible double redraw).
        const silentResizeReset = resizeFullResetRef.current;
        resizeFullResetRef.current = false;

        // Clear any stale Step 2 / resize flags so the upcoming animation calc
        // is not silently swallowed by a leftover noAnimationCalcRef=true that
        // Step 2 set in the same React batch as this config/data change.
        step2ExpansionRef.current = false;
        if (!silentResizeReset) {
            noAnimationCalcRef.current = false;
        }


        dataVersionRef.current += 1;
        setIsToggling(null);
        initialStateRef.current = null;
        isFirstCalculationRef.current = true;
        calcRetryRef.current = 0;
        calcRunIdRef.current += 1;
        cardRefs.current = new Array(displayCardsData.length);
        hasUserInteractedRef.current = false;
        prevExpandedRef.current = new Set();
        setScrollEnabled(false);
        entryAnimTimersRef.current.forEach(clearTimeout);
        entryAnimTimersRef.current = [];
        setEntryAnimStage(0);

        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
            autoRevertTimerRef.current = null;
        }

        setImageErrors({});

        if (displayCardsData.length === 0) {
            setCardStates([]);
            // Do NOT call setIsReady(true) here — it would paint the scroll container's background
            // colour (isDarkTheme ? #1a1a1a : #f0f0f0) for a fraction of a second before real data
            // arrives and resets isReady back to false, causing a visible background flash.
            // The container stays invisible (opacity:0 / visibility:hidden via shouldShowCards)
            // until the real-data animation calc sets setIsReady(true) in its RAF callback.
            // Do NOT set hasInitiallyLoadedRef.current = true here either — see Step 2 comment.
            setDisableBodyTransition(false);
            return;
        }

        isCalculatingRef.current = true;
        // On a zoom-driven reset keep the widget visible — blanking it (setIsReady(false))
        // is what makes the cards disappear and pop back in on every zoom step. The silent
        // calc that follows re-applies the final layout in place.
        if (!silentResizeReset) {
            setIsReady(false);
        }
        setDisableBodyTransition(true);
        setDisableHeaderTransition(true);
        const _collapsedCardState = {
            isExpanded: false,
            isHalfOpen: false,
            fullHeight: 0,
            visibleBodyHeight: 0,
            ExtraMargin: 0,
            renderMode: "collapsed",
            priorCompressedMode: "collapsed",
        };
        if (silentResizeReset) {
            // Preserve the existing card visuals (padded/truncated to the new count) so the
            // widget doesn't flash all-collapsed between this reset and the silent calc.
            // BUT refresh any open card's body height to the CURRENT dynamicOpenCardHeight
            // (which already reflects the new zoom width) — otherwise the open ticket shows
            // its stale, pre-zoom height during the ~200ms gap before the silent calc runs,
            // producing the inconsistent open-card height seen mid-resize.
            const _openBodyH = Math.floor(dynamicOpenCardHeight);
            setCardStates((prev) =>
                displayCardsData.map((_, index) => {
                    const p = prev[index];
                    if (!p) return _collapsedCardState;
                    if (p.renderMode === "open") {
                        return { ...p, fullHeight: _openBodyH, visibleBodyHeight: _openBodyH };
                    }
                    return p;
                })
            );
        } else {
            setCardStates(
                displayCardsData.map(() => ({ ..._collapsedCardState }))
            );
        }

        // Skip the entry animation when the initial card count is large (extreme zoom levels).
        // At 33% browser zoom, maxTicketsToRender can be 338+ cards. The 3-stage entry
        // animation (Stage 1 = all stripes → Stage 2 headers → Stage 3 bodies) takes ~8 seconds
        // with that many cards. Setting noAnimationCalcRef here tells calculateAndAdjustHeight
        // to use the silent path — applying the final layout immediately so open cards are
        // visible right away.
        //
        // A widget whose estimator count is below the total ticket count will end in
        // an expand-replay (and usually a silent overflow render) instead of an
        // immediate animation.  Its first calc + full-count re-render are heavy
        // (hundreds of cards) and the widget is hidden throughout — so defer the
        // whole pipeline until no parallel widget is playing its entry animation.
        const deferHeavyFirstCalc =
            !hasInitiallyLoadedRef.current &&
            displayCardsData.length < normalizedCardsData.length;

        const scheduleCalc = (delayMs = 0) => {
            const run = () => {
                const doCalc = () => requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
                const start = deferHeavyFirstCalc
                    ? () => _whenNoEntryAnimations(doCalc)
                    : doCalc;
                if (document.fonts && document.fonts.ready) {
                    document.fonts.ready.then(start).catch(start);
                } else {
                    start();
                }
            };
            if (delayMs > 0) return setTimeout(run, delayMs);
            run();
            return null;
        };

        // Stagger first-load calc so parallel widgets don't all race in the same frame.
        // A zoom-driven silent reset isn't a first load — run it promptly so zoom stays
        // responsive (the stagger delay is only to space out entry animations).
        const timer = scheduleCalc(
            (hasInitiallyLoadedRef.current || silentResizeReset) ? 0 : _getWidgetAnimStagger()
        );

        return () => {
            if (timer) clearTimeout(timer);
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
                autoRevertTimerRef.current = null;
            }
            if (revertAnimationTimerRef.current) {
                clearTimeout(revertAnimationTimerRef.current);
                revertAnimationTimerRef.current = null;
            }
            isRevertAnimatingRef.current = false;
            isEntryAnimatingRef.current = false;
            _entryAnimEnded(entryAnimCountedRef);
            entryAnimTimersRef.current.forEach(clearTimeout);
            entryAnimTimersRef.current = [];
        };
    }, [displayCardsData, minOpenTicketCount, isOpenTicketPlacementTop]);

    useEffect(() => {
        const triggerCalc = () => {
            const run = () => requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(run).catch(run);
            } else {
                run();
            }
        };

        const timer = setTimeout(() => {
            if (!hasInitiallyLoadedRef.current) {
                triggerCalc();
            }
        }, 300);

        const images = parentRef.current
            ? parentRef.current.querySelectorAll(".card img")
            : [];
        // Only re-trigger the calc if the initial load hasn't completed yet.
        // Image load events (e.g. Gravatars) can fire AFTER the first animation
        // has already set cards to medium/open state. Running calcAndAdjustHeight
        // at that point reads the medium header's min-height (22px) instead of the
        // collapsed min-height (3px), producing a corrupted "collapsed baseline" that
        // causes a wrong layout and a visible second re-render.
        const handleImageLoad = () => {
            if (!hasInitiallyLoadedRef.current) triggerCalc();
        };

        images.forEach((img) => {
            if (!img.complete) {
                img.addEventListener("load", handleImageLoad);
                img.addEventListener("error", handleImageLoad);
            }
        });

        return () => {
            clearTimeout(timer);
            images.forEach((img) => {
                img.removeEventListener("load", handleImageLoad);
                img.removeEventListener("error", handleImageLoad);
            });
        };
    }, [displayCardsData]);

    useEffect(() => {
        return () => {
            if (resizeDebounceRef.current) {
                clearTimeout(resizeDebounceRef.current);
            }
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
            }
            if (toggleAnimTimerRef.current) {
                clearTimeout(toggleAnimTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        calcHandlerRef.current = calculateAndAdjustHeight;
    });

    useLayoutEffect(() => {
        if (!isReady || displayCardsData.length === 0) return;

        const token = layoutMeasureTokenRef.current;
        if (token === appliedMeasureTokenRef.current) return;

        // measureAndApply is diagnostics-only (the correction pass was removed) but
        // walks EVERY card with getComputedStyle + getBoundingClientRect — hundreds
        // of forced-layout reads for large ticket counts.  Running it while entry
        // animations are playing (this widget's or parallel widgets') steals whole
        // frames and causes visible stutter.  Skip during the animation; the
        // animation-end handler bumps layoutMeasureTokenRef, so a fresh token
        // re-triggers this effect and the final settled layout still gets measured.
        // Same applies to user-toggle transitions: with large ticket counts the
        // measurement pass (and especially the overflow diagnostic) steals whole
        // frames from the 900ms open/close animation, causing visible stutter.
        if (isEntryAnimatingRef.current || isToggleAnimatingRef.current) {
            appliedMeasureTokenRef.current = token;
            return;
        }

        let settleTimer = null;
        let measureRetryCount = 0;
        let raf1 = 0;

        const measureAndApply = () => {
            const gridEl = gridRef.current;
            const viewportEl = scrollContainerRef.current || parentRef.current;
            if (!gridEl || !viewportEl) {
                appliedMeasureTokenRef.current = token;
                return;
            }

            if (isCalculatingRef.current) {
                if (measureRetryCount < 30) {
                    measureRetryCount += 1;
                    settleTimer = setTimeout(measureAndApply, 50);
                }
                return;
            }

            if (isRevertAnimatingRef.current) return;

            const domCardElements = Array.from(gridEl.querySelectorAll(":scope > .card"));
            const cardElements = (domCardElements.length > 0
                ? domCardElements
                : cardRefs.current.filter(Boolean)
            ).slice(0, displayCardsData.length);

            if (cardElements.length === 0) {
                appliedMeasureTokenRef.current = token;
                return;
            }

            if (cardElements.length < displayCardsData.length && measureRetryCount < 30) {
                measureRetryCount += 1;
                settleTimer = setTimeout(measureAndApply, 50);
                return;
            }

            if (cardElements.length < displayCardsData.length) {

                return;
            }


            appliedMeasureTokenRef.current = token;

            // Open card heights are now fixed at dynamicOpenCardHeight by the layout
            // calculation — no post-render DOM correction needed.  The correction was
            // removed because it caused two problems:
            //   1. Cards appeared cropped during the entry animation (correction only
            //      fired after the animation completed, ~2-3 s after load).
            //   2. Columns with more cards accumulated more sub-pixel rounding error
            //      from getBoundingClientRect, so they were corrected by a larger amount
            //      than sparse columns, making open-card heights unequal across columns.
            // The fit-check and applyOpenTicketLayout now agree on the exact height upfront.
        };

        // measureAndApply logs the DOM vs planned heights for diagnostics.
        // The correction pass has been removed — heights are fixed upfront.
        raf1 = requestAnimationFrame(measureAndApply);

        return () => {
            if (raf1) cancelAnimationFrame(raf1);
            if (settleTimer) clearTimeout(settleTimer);
        };
    }, [cardStates, isReady, displayCardsData.length, disableBodyTransition]);

    // One-shot height correction: when a card transitions to open, measure its real scrollHeight
    // and grow visibleBodyHeight if the pre-calculated estimate is too small. Fires only for
    // cards that just became open (wasOpen false → true) to avoid fighting measureAndApply.
    // Skipped on initial load (before any user toggle) so the viewport-fitted initial layout
    // is preserved and no spurious scrollbar appears.
    const prevExpandedRef = useRef(new Set());
    useLayoutEffect(() => {
        if (!isReady || !hasUserInteractedRef.current) return;
        const currentExpanded = new Set(
            cardStates.flatMap((s, i) => s.isExpanded ? [i] : [])
        );
        const newlyOpened = [...currentExpanded].filter(i => !prevExpandedRef.current.has(i));
        prevExpandedRef.current = currentExpanded;
        if (newlyOpened.length === 0) return;
        // Defer the scrollHeight read until AFTER the 900ms open transition settles:
        // reading it during the animation forces a full layout flush of the whole
        // grid (hundreds of cards), visibly hitching the transition.  scrollHeight
        // measures natural content height, which is independent of the animating
        // flex-basis, so the post-animation value is identical — and if a correction
        // is needed, it animates as a small follow-up grow once the card is open.
        const timer = setTimeout(() => {
            const fixes = {};
            newlyOpened.forEach(i => {
                const body = cardRefs.current[i]?.querySelector(".card-body");
                if (!body) return;
                const natural = body.scrollHeight;
                if (natural > Number(cardStates[i]?.visibleBodyHeight || 0) + 2) fixes[i] = natural;
            });
            if (Object.keys(fixes).length === 0) return;
            layoutMeasureTokenRef.current += 1;
            setCardStates(prev => prev.map((s, i) =>
                fixes[i] !== undefined && s.isExpanded
                    ? { ...s, visibleBodyHeight: fixes[i], fullHeight: fixes[i] }
                    : s
            ));
        }, 950);
        return () => clearTimeout(timer);
    }, [cardStates, isReady]);

    useLayoutEffect(() => {
        const dataKey = displayCardsData.map(c => c.id).join(',');
        if (prevHeaderDataKeyRef.current !== dataKey) {
            headerLabelModesRef.current = {};
            prevHeaderDataKeyRef.current = dataKey;
        }
        let changed = false;
        displayCardsData.forEach((card) => {
            const span = headerSpanRefs.current[card.id];
            if (!span) return;
            if (span.scrollWidth > span.clientWidth && (headerLabelModesRef.current[card.id] || 'full') === 'full') {
                headerLabelModesRef.current[card.id] = 'short';
                changed = true;
            }
        });
        if (changed) setHeaderLabelVersion(v => v + 1);
    }, [displayCardsData, headerLabelVersion]);

    const calculateAndAdjustHeight = () => {
        const skipAnimation = noAnimationCalcRef.current;
        noAnimationCalcRef.current = false;
        // Set to true when all cards in collapsed mode overflow the viewport.
        // Triggers scroll instead of trimming the card count.
        let overflowScroll = false;

        // ── Trigger-reason diagnostic ──────────────────────────────────────────
        // "RESIZE"  → silent recalc (noAnimationCalcRef was true): should never
        //             cause a full redraw; card states update in-place.
        // "INITIAL" → first load or data change: plays the entry animation.
        // If you see "INITIAL" triggered during a resize, check whether
        // maxTicketsToRender changed (which changes displayCardsData, which fires
        // the data-change useLayoutEffect and resets the widget).

        if (displayCardsData.length === 0) {
            // No cards — stay hidden. setIsReady(true) is intentionally omitted:
            // calling it here paints the scroll container's background colour with no cards,
            // causing a visible flash every time this calc fires on empty data (ResizeObserver
            // debounce, 300ms safety timer, viewport-prop change).
            setDisableBodyTransition(false);
            return;
        }

        const now = Date.now();
        const isInitialLoadStabilizing = !hasInitiallyLoadedRef.current;
        if (!isFirstCalculationRef.current && !isInitialLoadStabilizing && now - lastCalcTimeRef.current < 400) return;

        if (!parentRef.current || cardRefs.current.length === 0) {
            // Shares calcRetryRef with the missing-refs guard below; keep the same
            // generous budget so a large-widget retry chain isn't cut short here.
            if (calcRetryRef.current < 30) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            }
            return;
        }

        isFirstCalculationRef.current = false;
        lastCalcTimeRef.current = now;

        const parentHeight = parentRef.current.clientHeight;
        // Array.from densifies the slice: when the config changes, cardRefs.current is
        // rebuilt via `new Array(n)`, leaving sparse holes until refs repopulate. A bare
        // slice keeps those holes, and `.some`/`.map` SKIP holes (so the null-guard below
        // passes), but `for...of` yields `undefined` for them — crashing later at
        // getComputedStyle(_card). Densifying turns holes into `undefined` so the guard
        // catches them.
        const cardElements = Array.from(cardRefs.current.slice(0, displayCardsData.length));

        const containerHeight = Math.max(
            0,
            widgetViewportHeight || scrollContainerRef.current?.clientHeight || parentHeight
        );
        lastCalcContainerHRef.current = containerHeight;

        const primaryOpenBodyPx = dynamicOpenCardHeight;

        // Set the calculating flag BEFORE the card-elements null-check.
        // Without this, measureAndApply fires during the 100ms retry gap (because
        // isCalculatingRef.current is still false), sees stale open-card state from
        // the previous smaller render, and incorrectly shrinks cards from 175px → 159px.
        isCalculatingRef.current = true;

        if (cardElements.length === 0 || cardElements.some((el) => !el)) {
            // Refs not all populated yet. This is the expected transient state on a
            // config update — especially the large-count expand-replay path, where
            // cardRefs is rebuilt as `new Array(largeN)` and React needs several frames
            // to commit hundreds of card nodes (and run each ref callback). 5×100ms was
            // too short for big widgets, so the calc gave up before refs filled and the
            // widget stayed hidden (cards render at opacity:0 until setIsReady(true)) —
            // the empty-panel symptom. Give a much larger budget (≈3s).
            if (calcRetryRef.current < 30) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            } else {
                // Max retries exceeded — release the lock so measureAndApply
                // is not permanently blocked.
                isCalculatingRef.current = false;
            }
            return;
        }

        isMeasuringRef.current = true;
        setDisableBodyTransition(true);

        const currentCalcRunId = ++calcRunIdRef.current;
        requestAnimationFrame(() => {
            const restoreMeasuredBodies = () => { };

            if (currentCalcRunId !== calcRunIdRef.current) {
                // This inner RAF was superseded by a newer calc (step2-expand or trim
                // incremented calcRunIdRef between the outer RAF scheduling this and now).
                // The newer calc will handle setIsReady(true).  However, if rapid zoom
                // changes keep cancelling every inner RAF in a chain, the widget can stay
                // permanently hidden because no calc ever reaches setIsReady(true).
                // Guard against this: if the widget is still hidden after a stale
                // cancellation, schedule one more silent recovery attempt.
                restoreMeasuredBodies();
                isMeasuringRef.current = false;
                setDisableBodyTransition(false);
                if (!hasInitiallyLoadedRef.current || isCalculatingRef.current) {
                    // Another calc is already in flight — it will show the widget.
                    return;
                }
                // No active calc and widget might be hidden: schedule a recovery pass.
                // Use setTimeout(0) so we don't race with the React commit that cancelled us.
                setTimeout(() => {
                    if (currentCalcRunId === calcRunIdRef.current) {
                        // Nothing newer happened — we are safe to re-trigger.
                        noAnimationCalcRef.current = true;
                        isFirstCalculationRef.current = true;
                        calcHandlerRef.current?.();
                    }
                    // else: a newer calc already started — it will show the widget.
                }, 0);
                return;
            }

            const cardSpacing = cardElements.map((card) => {
                const styles = window.getComputedStyle(card);
                const marginBottom = parseFloat(styles.marginBottom || "0");
                return Number.isFinite(marginBottom) ? marginBottom : 0;
            });

            // Always use the pre-computed aspect-ratio height — no DOM measurement.
            // Reading body.scrollHeight caused two problems:
            //   1. On initial load, collapsed cards return their natural scrollHeight which
            //      varied per card, making the fit-check non-deterministic.
            //   2. On resize, open cards returned their inflated CSS height rather than
            //      the natural content height, under-counting how many open cards fit.
            // Using the fixed dynamicOpenCardHeight for every card keeps the fit-check
            // and applyOpenTicketLayout in perfect agreement — no post-render correction needed.
            const cardHeights = cardElements.map(() => primaryOpenBodyPx);

            restoreMeasuredBodies();
            isMeasuringRef.current = false;
            // Keep disableBodyTransition = true so the initial layout render has no
            // height animation. This ensures measureAndApply reads settled DOM heights
            // (not mid-animation values) when it fires 150ms later.
            // disableBodyTransition is set to false in toggleCard on first user interaction.

            const containerPadPx = 8;
            const availableHeight = containerHeight - (containerPadPx * 2);

            const _diagDpr = window.devicePixelRatio || 1;
            const _diagBorder = Math.round(_diagDpr) / _diagDpr;
            const _scenario = skipAnimation ? "RESIZE" : hasInitiallyLoadedRef.current ? "DATA-CHANGE" : "INITIAL-LOAD";

            const perCardHeight = availableHeight / Math.max(cardElements.length, 1);
            const compressionRatio = Math.max(0, Math.min(1, (90 - perCardHeight) / 60));
            const roomyRatio = 1 - compressionRatio;

            const headerPadPx = 2;
            const headerMinHeightPx = Math.max(2, Math.round(3 + (11 * roomyRatio)));
            const statusPadPx = Math.max(0, Math.round(4 * roomyRatio));
            const headerFontRem = Number((0.56 + (0.26 * roomyRatio)).toFixed(2));
            const statusLabelRem = 0.8;
            const statusValueRem = 1;
            // Responsive scaling anchored to the aspect-ratio reference point.
            // Reference width (371 px) is where dynamicOpenCardHeight ≈ 215 px (= 215 ÷ 0.58),
            // the point at which font sizes were originally designed to reach their maximum.
            // No upper clamp — beyond 371 px fonts keep growing at the same rate as card height,
            // so visual density stays constant across all zoom levels.
            const widthScale = Math.max(0, (widgetWidth - 150) / (371 - 150));
            const bodyPaddingPx = Math.round(4 + 4 * widthScale);           // 4 → 8 px
            const avatarSizePx = Math.round(32 + 18 * widthScale);          // 32 → 50 px
            const avatarFontPx = Math.round(12 + 6 * widthScale);           // 12 → 18 px
            const avatarWrapWidthPx = Math.round(54 + 26 * widthScale);     // 54 → 80 px
            const ownerFontRem = Number((0.70 + 0.15 * widthScale).toFixed(2));  // 0.70 → 0.85
            const topLeftFontRem = Number((0.80 + 0.20 * widthScale).toFixed(2)); // 0.80 → 1.00
            const titleFontRem = Number((1.10 + 0.40 * widthScale).toFixed(2));   // 1.10 → 1.50
            const descFontRem = Number((0.75 + 0.15 * widthScale).toFixed(2));   // 0.75 → 0.90
            const metaFontRem = Number((0.65 + 0.10 * widthScale).toFixed(2));   // 0.65 → 0.75
            const titleLines = 1;
            const descLines = 1;
            const showHeaderText = true;
            const normalHeaderFontRem = Number((0.72 + 0.18 * widthScale).toFixed(2)); // 0.72→0.90
            const headerLeftPadPx = Math.round(4 + 4 * widthScale);                    // 4→8 px
            const bodyRowGapPx = Math.max(2, Math.round(2 + 2 * widthScale));           // 2→4 px
            const centerRowPadPx = Math.max(1, Math.round(1 + 1 * widthScale));         // 1→2 px
            const centerRowMinHeightPx = Math.round(30 + 22 * widthScale);              // 30→52 px
            const titleMarginBottomRem = Number((0.10 + 0.05 * widthScale).toFixed(2)); // 0.10→0.15
            const descMarginBottomRem = Number((0.25 + 0.15 * widthScale).toFixed(2));  // 0.25→0.40
            const metaRowPadPx = Math.round(2 + 4 * widthScale);                        // 2→6 px

            // ── minOpenTicketCount semantics (0-99) ──────────────────────
            //   0    → render NO open cards (collapsed-only).
            //   1-98 → minimum number of open cards (clamped/floored).
            //   99   → "no maximum" — open as many as fit (legacy 0-behavior).
            const safeMinOpenCount = (() => {
                const n = Number(minOpenTicketCount);
                if (!Number.isFinite(n)) return 1;
                const floored = Math.floor(n);
                if (floored <= 0) return 0;
                if (floored >= 99) return 99;
                return Math.max(1, Math.min(98, floored));
            })();
            // noMaxMode: render as many open cards as fit (formerly safeMinOpenCount===0).
            // noOpenMode: render zero open cards — everything stays collapsed.
            const noMaxMode = safeMinOpenCount === 99;
            const noOpenMode = safeMinOpenCount === 0;

            const newCardStates = displayCardsData.map((_, index) => ({
                isExpanded: false,
                isHalfOpen: false,
                fullHeight: cardHeights[index] || 0,
                visibleBodyHeight: 0,
                ExtraMargin: 0,
                renderMode: "collapsed",
                priorCompressedMode: "collapsed",
            }));

            const totalSpacing = cardSpacing.slice(0, -1).reduce((sum, spacing) => sum + spacing, 0);

            setCompactStatusStyle((prev) => {
                if (
                    prev.headerPadPx === headerPadPx &&
                    prev.headerMinHeightPx === headerMinHeightPx &&
                    prev.statusPadPx === statusPadPx &&
                    prev.headerFontRem === headerFontRem &&
                    prev.normalHeaderFontRem === normalHeaderFontRem &&
                    prev.statusLabelRem === statusLabelRem &&
                    prev.statusValueRem === statusValueRem &&
                    prev.bodyPaddingPx === bodyPaddingPx &&
                    prev.avatarSizePx === avatarSizePx &&
                    prev.avatarFontPx === avatarFontPx &&
                    prev.ownerFontRem === ownerFontRem &&
                    prev.topLeftFontRem === topLeftFontRem &&
                    prev.titleFontRem === titleFontRem &&
                    prev.descFontRem === descFontRem &&
                    prev.metaFontRem === metaFontRem &&
                    prev.titleLines === titleLines &&
                    prev.descLines === descLines &&
                    prev.containerPadPx === containerPadPx &&
                    prev.avatarWrapWidthPx === avatarWrapWidthPx &&
                    prev.showHeaderText === showHeaderText &&
                    prev.headerLeftPadPx === headerLeftPadPx &&
                    prev.bodyRowGapPx === bodyRowGapPx &&
                    prev.centerRowPadPx === centerRowPadPx &&
                    prev.centerRowMinHeightPx === centerRowMinHeightPx &&
                    prev.titleMarginBottomRem === titleMarginBottomRem &&
                    prev.descMarginBottomRem === descMarginBottomRem &&
                    prev.metaRowPadPx === metaRowPadPx
                ) {
                    return prev;
                }
                return {
                    headerPadPx,
                    headerMinHeightPx,
                    statusPadPx,
                    headerFontRem,
                    normalHeaderFontRem,
                    statusLabelRem,
                    statusValueRem,
                    bodyPaddingPx,
                    avatarSizePx,
                    avatarFontPx,
                    ownerFontRem,
                    topLeftFontRem,
                    titleFontRem,
                    descFontRem,
                    metaFontRem,
                    titleLines,
                    descLines,
                    containerPadPx,
                    avatarWrapWidthPx,
                    showHeaderText,
                    headerLeftPadPx,
                    bodyRowGapPx,
                    centerRowPadPx,
                    centerRowMinHeightPx,
                    titleMarginBottomRem,
                    descMarginBottomRem,
                    metaRowPadPx,
                };
            });

            // ─── DOM-based dimension measurement ─────────────────────────────────
            // Read actual rendered element sizes from real DOM nodes.  This makes
            // every zoom level, device pixel ratio, OS font scale, and theme (dark/light
            // border width differences) automatically correct without any hardcoded math.
            //
            // All cards are currently in collapsed state (set by the full-reset path),
            // so we can measure the collapsed header directly.  The card-header border
            // is the same CSS rule for all render modes, so we derive medium/open heights
            // from the same measurement.
            const dpr = window.devicePixelRatio || 1;
            const _sampleCard = cardElements[0];
            // querySelector is more robust than firstElementChild — works regardless
            // of whether React inserts wrapper elements above the header.
            const _sampleHeader = _sampleCard?.querySelector('.card-header') ?? _sampleCard?.firstElementChild ?? null;

            // ── Read header dimensions from getComputedStyle (CSS px, DPR-aware) ──
            // CRITICAL: Do NOT rely on getBoundingClientRect() for element heights.
            // getBoundingClientRect() returns 0 when the widget container is hidden
            // (setIsReady(false) is called during every full-reset cycle), which would
            // make collapsedHeaderBlockPxForCalc = 0 and corrupt ALL layout calculations
            // — explaining the random "missing re-render" at any zoom level.
            //
            // getComputedStyle properties (borderTopWidth, minHeight, etc.) always return
            // the correct CSS-computed value regardless of element visibility, so we
            // derive the exact rendered height from those instead.
            let _headerBorderTop = 0, _headerBorderBottom = 0;
            if (_sampleHeader) {
                const _hs = window.getComputedStyle(_sampleHeader);
                _headerBorderTop = parseFloat(_hs.borderTopWidth) || 0;
                _headerBorderBottom = parseFloat(_hs.borderBottomWidth) || 0;
            }
            const headerBorderExtraPx = _headerBorderTop + _headerBorderBottom;

            // Collapsed header height = JSX constant (3px min-height) + border.
            // CRITICAL: do NOT read minHeight from the DOM for this constant.
            // During resize calcs the cards are still in their previous rendered state —
            // cardElements[0] is often in "open" mode (TOP placement) with minHeight=22px.
            // Reading that gives collapsedHeaderBCR≈24px instead of the correct ≈5px,
            // inflating baseline by 5x (e.g. 20×24=476px vs 20×5=100px) and triggering
            // a massive wrong trim (20→8 cards). collapsedHeaderMinHeightPx=3 is a JSX
            // constant (never changes with card state or zoom).
            const _collapsedJsxMinHeightPx = 3; // matches collapsedHeaderMinHeightPx in JSX
            const collapsedHeaderBCR = _collapsedJsxMinHeightPx + headerBorderExtraPx;

            // ── Read card gap from any medium/open card that happens to be visible ──
            // marginBottom is 0 for collapsed, positive for medium/open.
            // getComputedStyle marginBottom IS affected by display:none, so we use the
            // theme default as the baseline and only override when a live value is available.
            let _measuredGapPx = isDarkTheme ? 5 : 3;
            for (const _card of cardElements) {
                const _mb = parseFloat(window.getComputedStyle(_card).marginBottom) || 0;
                if (_mb > 0) { _measuredGapPx = _mb; break; }
            }

            // ── Derive all layout constants from DOM measurements ──
            const normalHeaderPadPxForCalc = 2;          // CSS 2px top + 2px bottom (fixed in JSX)
            // ── Font-driven header content height ────────────────────────────────
            // ROOT CAUSE of the aggregate stack overflow: normalHeaderMinHeightPxForCalc
            // was a flat 22px constant — but the rendered header's actual content
            // height is governed by `normalHeaderFontRem` (line ~1065), which scales
            // via `widthScale = (widgetWidth - 150) / (371 - 150)` with NO upper clamp
            // (see comment at widthScale's definition: "fonts keep growing... beyond
            // 371px"). At extreme zoom (e.g. widgetWidth≈3056px), widthScale ≈ 13,
            // producing normalHeaderFontRem ≈ 3.1rem (~50px font, ~59px line box at
            // lineHeight 1.2) — nearly 3× the assumed 22px. Every medium/open card
            // then rendered ~37px taller than budgeted; across just 3 such cards
            // (1 open + 2 medium in the reported log) that alone accounts for the
            // ~111px discrepancy behind the observed 75px aggregate overflow.
            //
            // Fix: derive the header's minimum content height from the SAME font
            // metrics the header actually renders with (fontSize × lineHeight),
            // floored at the textbook 22px so normal-density layouts are unaffected.
            const _rootFontSizePx = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
            const _normalHeaderLineHeight = 1.2; // matches headerLineHeight for normal/medium mode in JSX
            const _fontDrivenHeaderContentPx = Math.ceil(normalHeaderFontRem * _rootFontSizePx * _normalHeaderLineHeight);
            const normalHeaderMinHeightPxForCalc = Math.max(22, _fontDrivenHeaderContentPx);
            const normalHeaderBorderPxForCalc = headerBorderExtraPx;
            const collapsedHeaderBlockPxForCalc = collapsedHeaderBCR; // exact DOM measurement
            // Normal/medium header = content(max(22, font-driven)) + pad(4) + border(measured)
            const normalHeaderBlockPxForCalc = normalHeaderMinHeightPxForCalc
                + (normalHeaderPadPxForCalc * 2)
                + normalHeaderBorderPxForCalc; // e.g. 30.8px at DPR=1.25 vs 28px at DPR=1

            // ── Feed measurements back to estimator (state update, deferred) ──
            const _newDims = {
                collapsedHeaderH: collapsedHeaderBCR,
                normalHeaderH: normalHeaderBlockPxForCalc,
                headerBorderEach: _headerBorderTop,
                headerBorderTotal: headerBorderExtraPx,
                cardDivBorderTotal: headerBorderExtraPx, // card div uses same CSS border
                gap: _measuredGapPx,
                dpr,
                isDarkTheme,
                containerH: containerHeight,
                availableH: availableHeight,
            };
            // Update the ref directly — no re-render needed.  The estimator will use
            // these values on its next natural re-run (triggered by data/settings changes).
            measuredDimsRef.current = _newDims;

            // ── DOM-measured dimensions (key source-of-truth for fit-check) ──
            // DIVERGENCE CHECK: compare estimator values (DPR-formula) vs DOM-measured values.
            // If these differ across initial-load / resize / zoom, the estimator will allocate
            // the wrong number of cards, causing a Step-2 reset or wrong open count.
            const _estimDprNow = window.devicePixelRatio || 1;
            const _estimBorderNow = Math.round(_estimDprNow) / _estimDprNow;
            const _estimCollapsedNow = Math.max(3, 3 + 2 * _estimBorderNow);
            const _estimNormalHNow = 22 + 4 + 2 * _estimBorderNow;
            const _domVsEstimMismatch =
                Math.abs(collapsedHeaderBCR - _estimCollapsedNow) > 0.5 ||
                Math.abs(normalHeaderBlockPxForCalc - _estimNormalHNow) > 0.5;


            const compressedHeaderHeight = collapsedHeaderBlockPxForCalc;
            // Baseline: all N cards at collapsed height with NO inter-card spacing.
            // Collapsed cards render with 0px marginBottom, so totalSpacing from the DOM
            // represents the PREVIOUS render state (medium cards may have had 3–5px gaps).
            // Including totalSpacing inflates the baseline when transitioning from a
            // medium-heavy state, making the budget look tighter than it is, and causes
            // a different overflow when transitioning from all-collapsed (totalSpacing=0)
            // to many-medium cards (each gains ~3–5px margin not counted in the delta).
            // Fix: baseline uses only N × collapsedHeight (no spacing), and every upgrade
            // delta below includes defaultEstimatedCardSpacingPx to cover the margin gain.
            const collapsedBaselineHeight = cardElements.length * compressedHeaderHeight;
            let availableBodyHeight = Math.max(0, availableHeight - collapsedBaselineHeight);

            // ── Baseline overflow / open-card squeeze detection ──────────────────
            // Three conditions require reducing maxTicketsToRender:
            //
            //   1. Baseline overflow: collapsed baseline alone > availableHeight
            //      → fit-check can never open any cards.
            //
            //   2. Open-card squeeze: availableBodyHeight (= availableHeight − baseline)
            //      < cost of a single open ticket.
            //
            // These two are checked here (before fittedOpenCount is computed).
            // A third condition — fittedOpenCount < targetOpenCount — is checked
            // AFTER the fit-check loop below.
            //
            // The skipAnimation gate has been removed so trim fires even on animated
            // initial loads.  When trim fires it sets noAnimationCalcRef=true so the
            // follow-up calc applies the correct layout silently.
            {
                // mediumCardBorderPxForCalc and cardGapPx not computed yet; inline here.
                const _border = headerBorderExtraPx;
                const _gap = _measuredGapPx;
                const _mediumUpgrade = Math.max(0, normalHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc) + _gap + _border;

                // Minimum body budget to open at least 1 ticket (header delta + body + border).
                const singleOpenCostPx = (
                    Math.floor(dynamicOpenCardHeight) +
                    (normalHeaderBlockPxForCalc - compressedHeaderHeight) +
                    defaultEstimatedCardSpacingPx +
                    _border
                );
                const baselineOverflows = collapsedBaselineHeight > availableHeight;
                const noRoomForOpenCards = availableBodyHeight < singleOpenCostPx;

                if (baselineOverflows || noRoomForOpenCards) {
                    // Cards exceed viewport — enable scroll so all tickets remain visible.
                    // The fit-check loop below will set fittedOpenCount to whatever fits (may be 0),
                    // and applyCompressedModes will collapse remaining cards into a scrollable stack.
                    const reason = baselineOverflows ? "baseline-overflow" : "open-card-squeeze";

                    overflowScroll = true;
                }
            }

            // ─── Reserve space for guaranteed medium (orphan) header upgrades ────
            // Collapsed cards have no card border and 0px marginBottom.  When promoted to
            // medium/open they gain a DPR-corrected card border and cardGapPx marginBottom.
            // Card div border (absent on collapsed, present on medium/open).
            // DPR-corrected to match actual rendered border at non-integer zoom levels.
            // Card-div border: same CSS `border: 1px solid` as header, same DPR rendering.
            const mediumCardBorderPxForCalc = headerBorderExtraPx;
            // Card gap: use DOM-measured value if a medium/open card was visible,
            // otherwise fall back to theme default.  Dark theme adds 2px extra margin.
            const cardGapPx = _measuredGapPx;
            const mediumUpgradePerCardPx = Math.max(0, normalHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc) + cardGapPx + mediumCardBorderPxForCalc;
            // ≈ (28-5) + 3 + 2 = 28 px (light, DPR=1); (28-5) + 5 + 2 = 30 px (dark)
            const potentialMediumCount = Math.min(Math.max(0, cardElements.length - 1), MIN_MEDIUM_HEADERS);
            const reservedMediumUpgradePx = potentialMediumCount * mediumUpgradePerCardPx;

            // Open tickets have first priority — they compete for the full available budget.
            // Orphan headers (medium) take whatever remains after open cards are placed.
            // Pre-reserving medium space here reduces the open-card budget and can show
            // fewer open tickets than the space actually allows.
            const availableBodyForOpenFit = availableBodyHeight;

            const fullOpenFallbackPx = dynamicOpenCardHeight;

            // Use displayCardsData.length (not cardElements.length) for the "bottom"
            // end index.  If a previous trim left cardRefs shorter than the current
            // displayCardsData, cardElements.length would be too small and open cards
            // would land in the middle of the rendered list instead of the bottom.
            // displayCardsData.length is always the true number of rendered cards.
            const buildOpenIndexes = (count) => (count > 0
                ? (isOpenTicketPlacementTop
                    ? Array.from({ length: count }, (_, idx) => idx)
                    : Array.from({ length: count }, (_, idx) => displayCardsData.length - count + idx))
                : []);

            const targetOpenCount = noOpenMode
                ? 0
                : (noMaxMode ? displayCardsData.length : Math.min(safeMinOpenCount, displayCardsData.length));
            // Always start from targetOpenCount (respects minOpenTicketCount).
            // Never open more cards than requested just because there is extra vertical
            // space — the surplus is already distributed as additional body height inside
            // applyOpenTicketLayout.  The old "allCardsFitOpen → open everything" branch
            // ignored minOpenTicketCount entirely when the widget was tall.
            let fittedOpenCount = targetOpenCount;
            // index.css forces box-sizing: content-box !important on every element.
            // The card-body flex-basis is set to (visualBodyHeight − 2 × bodyPaddingPx) so
            // that content-box adds the padding back, giving visual height = dynamicOpenCardHeight.
            // The budget here therefore treats dynamicOpenCardHeight as the VISUAL body height
            // (padding already factored into the flex-basis adjustment in JSX).
            // Only the outer card div border (1px solid → 2px top+bottom) is additional overhead.
            // This constant must match bodyPadOverhead inside applyOpenTicketLayout below.
            const fitCheckBodyPadOverhead = mediumCardBorderPxForCalc; // = 2 px (card div border only)
            while (fittedOpenCount > 0) {
                const candidateIndexes = buildOpenIndexes(fittedOpenCount);
                const candidateOpenHeights = candidateIndexes.reduce((sum, idx) => {
                    const fullHeight = Math.max(1, Math.floor(cardHeights[idx] || fullOpenFallbackPx));
                    return sum + fullHeight;
                }, 0);
                // In no-max mode (noMaxMode), gaps exist only BETWEEN open cards
                // (N-1 gaps for N cards), not per card. The normal N*gap is conservative but
                // overestimates by 1 gap (~5px), which can incorrectly reject a valid open count.
                const gapCount = noMaxMode
                    ? Math.max(0, candidateIndexes.length - 1)
                    : candidateIndexes.length;
                const candidateHeaderCost = candidateIndexes.length * (normalHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc)
                    + gapCount * defaultEstimatedCardSpacingPx;
                const candidatePadCost = fitCheckBodyPadOverhead * candidateIndexes.length;
                // Use the medium-reserve-adjusted budget so orphan headers are
                // guaranteed to fit alongside however many cards are open.
                if ((candidateOpenHeights + candidateHeaderCost + candidatePadCost) <= availableBodyForOpenFit) break;
                fittedOpenCount -= 1;
            }

            // Per-card open cost = body + (normalH−collapsedH+gap) + border  [net upgrade above collapsed baseline]
            const _openCostPerCard = Math.floor(dynamicOpenCardHeight)
                + (normalHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc)
                + (fittedOpenCount > 0 ? Math.max(0, fittedOpenCount - 1) / fittedOpenCount * defaultEstimatedCardSpacingPx : defaultEstimatedCardSpacingPx)
                + mediumCardBorderPxForCalc;

            // Condition 3 (fit-check couldn't place all requested open cards) is intentionally
            // not trimming anymore.  Accept the reduced fittedOpenCount so all collapsed cards
            // remain visible in the scrollable stack.
            if (fittedOpenCount < targetOpenCount) {

                overflowScroll = overflowScroll || (collapsedBaselineHeight > availableHeight);
            }

            const openIndexes = buildOpenIndexes(fittedOpenCount);
            const openIndexSet = new Set(openIndexes);
            const openCardsMeta = openIndexes.map((idx) => ({
                idx,
                fullHeight: Math.max(1, Math.floor(cardHeights[idx] || fullOpenFallbackPx)),
            }));

            const applyOpenTicketLayout = (openBodyBudgetPx) => {
                const safeBudget = Math.max(0, Math.floor(openBodyBudgetPx));
                const openCount = openCardsMeta.length;
                if (openCount === 0 || safeBudget <= 0) return 0;

                // index.css forces content-box globally; flex-basis in JSX is pre-adjusted
                // to (visualBodyHeight − padding_TB) so that content-box restores the visual
                // height to exactly dynamicOpenCardHeight (the budget value).
                // Only the outer card div border (2px) is additional overhead here.
                // Must match fitCheckBodyPadOverhead above so fit-check and layout agree.
                const bodyPadOverhead = mediumCardBorderPxForCalc; // = 2 px (card div border only)

                // All open cards always render at exactly dynamicOpenCardHeight — never scaled
                // down by budget.  The fit-check above already confirmed this height fits;
                // using a smaller value here would make columns with more cards render shorter
                // open cards than columns with fewer cards (unequal visual height across columns).
                //
                // Floor to an integer so that budget consumption matches DOM measurements exactly.
                // dynamicOpenCardHeight is a decimal (e.g. 208.8), and the fit-check uses
                // Math.floor(cardHeights[idx]) = 208.  Without flooring here, the 0.8 px
                // sub-pixel remainder (per card) accumulates: for 2 open cards that is 1.6 px
                // of body cost that isn't charged to availableBodyHeight, leaving a 1.6 px gap
                // that applyCompressedModes uses to squeeze in one extra medium card that
                // doesn't actually fit — causing a 2 px at-rest overflow at the bottom.
                const uniformBodyHeight = Math.floor(dynamicOpenCardHeight);

                openCardsMeta.forEach((item) => {
                    newCardStates[item.idx].isExpanded = true;
                    newCardStates[item.idx].isHalfOpen = false;
                    newCardStates[item.idx].visibleBodyHeight = uniformBodyHeight;
                    newCardStates[item.idx].renderMode = "open";
                });

                // uniformBodyHeight is already Math.floor(dynamicOpenCardHeight) — integer.
                // bodyPadOverhead = 2 (integer).  Actual consumption is exact; no need to
                // clamp to safeBudget (which can round down and create a sub-pixel gap).
                return uniformBodyHeight * openCount + bodyPadOverhead * openCount;
            };

            // Hoisted so it is accessible in the Step-2 "show more cards" check below.
            let consumedOpenBudgetPx = 0;
            if (openIndexes.length > 0) {
                // Open card header cost must match candidateHeaderCost in the fit-check.
                // No-max mode uses (N-1) gaps; regular mode uses N gaps.
                const _openGapsForBudget = noMaxMode
                    ? Math.max(0, openIndexes.length - 1)
                    : openIndexes.length;
                const openHeaderBudgetPx = openIndexes.length * (normalHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc)
                    + _openGapsForBudget * defaultEstimatedCardSpacingPx;
                const openBodyBudgetPx = Math.max(0, availableBodyForOpenFit - openHeaderBudgetPx);
                const consumedOpenBodyPx = applyOpenTicketLayout(openBodyBudgetPx);
                consumedOpenBudgetPx = openHeaderBudgetPx + consumedOpenBodyPx;
                // Update the full availableBodyHeight (not the adjusted one) so the
                // reserved medium upgrade budget remains available for applyCompressedModes.
                availableBodyHeight = Math.max(0, availableBodyHeight - consumedOpenBudgetPx);
            }

            const remainingIndexes = [];
            for (let idx = 0; idx < cardElements.length; idx++) {
                if (!openIndexSet.has(idx)) {
                    remainingIndexes.push(idx);
                }
            }

            // ─────────────────────────────────────────────────────────────────
            // applyCompressedModes  (CHANGED)
            //
            // Layout rule for non-open cards:
            //   1. Always show at least MIN_MEDIUM_HEADERS as "medium" (visible
            //      header text), capped by the actual number of non-open cards.
            //   2. If budget allows more medium headers beyond the guarantee,
            //      add them using the existing weight-based budget.
            //   3. All remaining cards become "collapsed" (stack / highly-
            //      collapsed headers — tiny colour stripe only).
            //
            // This ensures: open tickets → medium headers (≥3) → collapsed stack
            // ─────────────────────────────────────────────────────────────────
            const applyCompressedModes = (indexes, remainingBodyHeightPx) => {

                const nonOpenCount = indexes.length;

                if (nonOpenCount === 0) return 0;

                // Cost of promoting one card from collapsed to medium: header height delta
                // + spacing gained + card border gained (collapsed has no border, medium has 2px).
                // Must match mediumUpgradePerCardPx exactly so reserve and allocator agree.
                // mediumCardBorderPxForCalc = 2 is defined in the outer calculateAndAdjustHeight scope.
                const mediumUpgradePx = Math.max(1, (normalHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc) + cardGapPx + mediumCardBorderPxForCalc);
                // ≈ (28-5) + 3 + 2 = 28 px light / 30 px dark  (matches mediumUpgradePerCardPx)

                // Budget available for medium upgrades = remaining above-baseline space
                // after open cards are accounted for.
                // Previously this used (totalRemainingSpace × 0.33) where
                // totalRemainingSpace = remainingBodyHeightPx + collapsedBaselineHeight.
                // With a large collapsedBaselineHeight (many collapsed cards), that
                // formula could produce a mediumCount larger than the actual budget,
                // causing the open card to overflow the viewport.
                // Using remainingBodyHeightPx directly (matching the landscape file)
                // keeps mediumCount strictly within the available budget.
                const dynamicSpaceForCompressedModes = Math.max(0, remainingBodyHeightPx);
                const maxMediumByBudget = Math.min(nonOpenCount, Math.floor(dynamicSpaceForCompressedModes / mediumUpgradePx));

                // Guarantee MIN_MEDIUM_HEADERS orphan headers — but only when the
                // remaining space can actually afford them.  If it cannot (e.g. the
                // open cards already consumed most of the viewport), fall back to the
                // budget-based count so the layout doesn't overflow.
                const canAffordGuarantee = dynamicSpaceForCompressedModes >= (MIN_MEDIUM_HEADERS * mediumUpgradePx);
                const guaranteedMedium = canAffordGuarantee ? Math.min(nonOpenCount, MIN_MEDIUM_HEADERS) : 0;

                // Final count: honour the guarantee when affordable; also take any extra budget
                const mediumCount = Math.max(guaranteedMedium, maxMediumByBudget);


                // Allocation order: closest non-open cards to the open ones first.
                // "top" placement → allocate from first non-open upward
                // "bottom" placement → allocate from last non-open downward
                const allocationOrder = isOpenTicketPlacementTop
                    ? indexes
                    : [...indexes].reverse();

                //let findFirstLastMedium = false;
                allocationOrder.forEach((idx, orderIdx) => {
                    const shouldBeMedium = orderIdx < mediumCount;
                    newCardStates[idx].lastCollapsedMode = false;

                    // Mark the first card that transitions from medium → collapsed
                    // so the spacing logic can insert a small gap there.
                    // if (!shouldBeMedium && !findFirstLastMedium) {
                    //     findFirstLastMedium = true;
                    //     console.log("First collapsed-stack card at index", idx);
                    //     newCardStates[idx].lastCollapsedMode = true;
                    // }

                    newCardStates[idx].isExpanded = false;
                    newCardStates[idx].isHalfOpen = false;
                    newCardStates[idx].visibleBodyHeight = 0;
                    // "medium"    → normal-sized header with visible label text
                    // "collapsed" → tiny colour stripe (stack / highly-collapsed)
                    newCardStates[idx].renderMode = shouldBeMedium ? "medium" : "collapsed";
                    newCardStates[idx].priorCompressedMode = shouldBeMedium ? "medium" : "collapsed";
                });

                const consumedBudgetPx = mediumCount * mediumUpgradePx;
                return Math.max(0, remainingBodyHeightPx - consumedBudgetPx);


            };

            applyCompressedModes(remainingIndexes, availableBodyHeight);

            // ── Remaining-space distribution ──────────────────────────────────────
            // The budget model uses defaultEstimatedCardSpacingPx (5 px) for gaps
            // regardless of theme, which over-reserves for light theme (3 px actual).
            // It also intentionally leaves ~8 px of slack so the fit-check never
            // clips the last card.  After all card modes are settled, compute how
            // much space is genuinely unused with the real gap and use it in two steps:
            //
            //   Step 1 — Promote collapsed → medium (priority: closest to open first).
            //     Showing more ticket headers is far more useful than a blank open body.
            //     Mirrors applyCompressedModes allocation order exactly so promoted cards
            //     appear in a contiguous block adjacent to the existing medium cluster.
            //
            //   Step 2 — Distribute any leftover to open card bodies.
            //     Only space that could not buy another medium header goes here.
            //
            // Together these ensure the layout fills to within the configured 8 px grid
            // margin regardless of widget size, theme, or number of open/collapsed cards.
            if (openIndexes.length > 0) {
                const actualGapPx = cardGapPx; // DOM-measured gap (or theme default)

                // Sum actual DOM height of every card + its trailing gap.
                // Collapsed cards carry no gap (they stack flush).
                // The last card in the list also carries no gap.
                let actualUsedPx = 0;
                newCardStates.forEach((cs, idx) => {
                    const isLast = idx === newCardStates.length - 1;
                    const gap = (isLast || cs.renderMode === "collapsed") ? 0 : actualGapPx;
                    if (cs.renderMode === "open") {
                        actualUsedPx += mediumCardBorderPxForCalc   // 2 px card border
                            + normalHeaderBlockPxForCalc              // 28 px header
                            + cs.visibleBodyHeight                    // body visual height
                            + gap;
                    } else if (cs.renderMode === "medium") {
                        actualUsedPx += mediumCardBorderPxForCalc + normalHeaderBlockPxForCalc + gap;
                    } else {
                        actualUsedPx += collapsedHeaderBlockPxForCalc; // 5 px, no gap
                    }
                });
                let remainingPx = availableHeight - actualUsedPx;
                // Capture slack BEFORE Step 1 promotions consume it.
                // Step 2 must see the full available slack to correctly compute how many
                // new collapsed cards can be added — Step 1 promotions are medium upgrades
                // of EXISTING cards and do not create space for additional cards.
                const remainingPxBeforeStep1 = remainingPx;

                // ── Step 1: Promote collapsed → medium ────────────────────────────
                // Promotion order mirrors applyCompressedModes: closest non-open card
                // to the open ones gets promoted first so the medium cluster stays
                // contiguous.  For bottom placement the open card(s) are last, so we
                // traverse collapsed indexes descending; for top placement, ascending.
                let promotionCount = 0;
                if (remainingPx >= 1) {
                    const collapsedIdxs = [];
                    newCardStates.forEach((cs, i) => {
                        if (cs.renderMode === "collapsed") collapsedIdxs.push(i);
                    });
                    // Closest-to-open first: bottom → descending, top → ascending
                    const promoOrder = isOpenTicketPlacementTop
                        ? collapsedIdxs
                        : [...collapsedIdxs].reverse();

                    for (const idx of promoOrder) {
                        if (remainingPx < 1) break;
                        const isLast = idx === newCardStates.length - 1;
                        // Cost of turning this collapsed stripe into a medium header:
                        // gain a border (2px) + normal header (28px) + gap (3 or 5px if not last)
                        // and lose the collapsed stripe height (5px).
                        const gap = isLast ? 0 : actualGapPx;
                        const cost = mediumCardBorderPxForCalc + normalHeaderBlockPxForCalc
                            + gap - collapsedHeaderBlockPxForCalc;
                        if (remainingPx >= cost) {
                            newCardStates[idx].renderMode = "medium";
                            newCardStates[idx].priorCompressedMode = "medium";
                            remainingPx -= cost;
                            promotionCount++;
                        } else {
                            // Can't afford the next promotion — stop to keep the cluster
                            // contiguous rather than leaving a hole in the stack.
                            break;
                        }
                    }
                }

                // ── Step 2: show more cards if budget and space allow ─────────────
                // Open card bodies are NEVER inflated.  Instead, any leftover space is
                // used to pull in additional tickets from normalizedCardsData as
                // collapsed (stack-header) or medium entries.
                //
                // Safety rule: adding k more collapsed cards reduces
                //   availableBodyForOpenFit by k × 5 px.
                // We must not reduce it below openCost — otherwise fittedOpenCount
                // would shrink on the next call.  The same formula used by
                // estimatedTicketsToRender (Step 2) gives the safe ceiling:
                //
                //   safeMaxTotal = ⌊(availableHeight − consumedOpenBudgetPx − MIN_MEDIUM_HEADERS × 30) / 5⌋
                //
                // consumedOpenBudgetPx matches the estimator's openFitCostPx exactly
                // (both equal fittedOpenCount × (headerDelta + dynOpenH + border)).
                const moreCardsAvailable = normalizedCardsData.length - displayCardsData.length;


                if (moreCardsAvailable > 0 && remainingPxBeforeStep1 >= collapsedHeaderBlockPxForCalc) {
                    // Maximum total cards N such that fittedOpenCount open cards still fit.
                    // Formula mirrors estimatedTicketsToRender Step 2 exactly:
                    //   availableBodyForOpenFit(N) = availableHeight − N×5 − reservedMedium ≥ openCost
                    // → N ≤ (availableHeight − consumedOpenBudgetPx − reservedMedium) / 5
                    //
                    // Use the actual potentialMediumCount (= min(N-1, MIN_MEDIUM_HEADERS))
                    // for the reserve; with few cards the reserve is smaller, allowing
                    // more total cards before the fit-check fails.  Capped at the value
                    // for the worst-case (all future cards are non-open) to stay safe.
                    const futureMediumReserve = Math.min(
                        (cardElements.length + moreCardsAvailable - openIndexes.length),
                        MIN_MEDIUM_HEADERS
                    ) * mediumUpgradePerCardPx;
                    const safeMaxTotal = Math.max(
                        openIndexes.length,
                        Math.floor(
                            Math.max(0, availableHeight - consumedOpenBudgetPx - futureMediumReserve)
                            / collapsedHeaderBlockPxForCalc
                        )
                    );
                    // How many more the BUDGET allows AND the remaining DOM space can hold.
                    const safeByBudget = Math.max(0, safeMaxTotal - cardElements.length);
                    // Use pre-Step-1 slack: Step 1 promotes existing cards (collapsed→medium)
                    // and does not affect how many NEW collapsed cards can fit.
                    const safeBySpace = Math.floor(remainingPxBeforeStep1 / collapsedHeaderBlockPxForCalc);
                    // In no-max mode, cap Step 2 additions to estimatedTicketsToRender to
                    // prevent an oscillation loop with Condition 0 no-max-trim.
                    const safeByEstimate = noMaxMode
                        ? Math.max(0, estimatedTicketsToRender - cardElements.length)
                        : moreCardsAvailable;
                    const canAdd = Math.min(moreCardsAvailable, safeByBudget, safeBySpace, safeByEstimate);

                    // OVERFLOW SHORT-CIRCUIT: if we cannot add ALL remaining tickets
                    // (canAdd < moreCardsAvailable), the widget genuinely overflows. The
                    // incremental Step-2 crawl (adding only `canAdd` per pass) is meant to
                    // discover the no-scroll fit limit — but for a large ticket count it
                    // stalls/oscillates a few cards at a time and never hands off to the
                    // overflow block below, so the widget shows only a handful (or none)
                    // of the tickets after a config update. When overflow is certain, skip
                    // the crawl entirely and fall through to the overflow block at the end
                    // of this calc, which loads the FULL count once with scroll enabled.
                    const allRemainingFit = canAdd >= moreCardsAvailable;
                    if (!allRemainingFit) {

                    }

                    if (canAdd > 0 && allRemainingFit) {
                        // ⚠ Step 2 pre-init = estimator under-counted (DPR mismatch) → animation restarts
                        if (!hasInitiallyLoadedRef.current) {

                            isMeasuringRef.current = false;
                            isCalculatingRef.current = false;
                            // Signal the useLayoutEffect to skip the full-reset (setIsReady(false)
                            // + animation replay) — just expand card refs and re-run the initial calc.
                            preInitStep2ExpansionRef.current = true;
                            setMaxTicketsToRender(prev =>
                                Math.min(normalizedCardsData.length, prev + canAdd)
                            );
                            return;
                        } else {
                            // After initial load: expand card count and return early.
                            // The useLayoutEffect step2ExpansionRef path detects this
                            // as a pure extension of existing data (not a real data change)
                            // and schedules a silent recalculation WITHOUT replaying the
                            // entry animation.  Returning early here prevents setCardStates
                            // and setIsReady from being called with the old (stale) card
                            // count, which was the source of the spurious full-reset.
                            isMeasuringRef.current = false;
                            isCalculatingRef.current = false;
                            noAnimationCalcRef.current = true;
                            isFirstCalculationRef.current = true;
                            step2ExpansionRef.current = true;
                            setMaxTicketsToRender(prev =>
                                Math.min(normalizedCardsData.length, prev + canAdd)
                            );
                            return;
                        }
                    }
                }

                const finalOpenBodyH = newCardStates[openIndexes[0]]?.visibleBodyHeight ?? 0;
                const openCount = newCardStates.filter(s => s.renderMode === "open").length;
                const mediumCount2 = newCardStates.filter(s => s.renderMode === "medium").length;
                const collapsedCount = newCardStates.filter(s => s.renderMode === "collapsed").length;
                const leftoverPx = availableHeight - actualUsedPx;
                const nextCollapsedPx = collapsedHeaderBlockPxForCalc; // 5 px

                // ── Layout diagnostic ─────────────────────────────────────────────
                // All quantities the user asked for, plus the structural-gap note.

            }
            // ── End remaining-space distribution ─────────────────────────────────

            // ── Post-layout DOM-prediction audit ──────────────────────────────────
            // Predicts the actual DOM height using real gap values and the final
            // (post-distribution) body heights stored in newCardStates.  The result
            // should now be ≤ containerHeight and within 1–2 px of it.
            {
                const auditGapPx = cardGapPx; // DOM-measured gap (same source as layout calc)
                const auditOpenBodyH = openIndexes.length > 0
                    ? (newCardStates.find(cs => cs.renderMode === "open")?.visibleBodyHeight
                        ?? Math.floor(dynamicOpenCardHeight))
                    : Math.floor(dynamicOpenCardHeight);
                let auditContentPx = 0;
                newCardStates.forEach((cs, idx) => {
                    const isLast = idx === newCardStates.length - 1;
                    const gap = (isLast || cs.renderMode === "collapsed") ? 0 : auditGapPx;
                    if (cs.renderMode === "open") {
                        auditContentPx += mediumCardBorderPxForCalc + normalHeaderBlockPxForCalc
                            + cs.visibleBodyHeight + gap;
                    } else if (cs.renderMode === "medium") {
                        auditContentPx += mediumCardBorderPxForCalc + normalHeaderBlockPxForCalc + gap;
                    } else {
                        auditContentPx += collapsedHeaderBlockPxForCalc; // 5 px
                    }
                });
                const auditTotal = Math.ceil(auditContentPx) + 16; // +16 for grid margin
                const overBy = auditTotal - containerHeight;
                const status = overBy > 0
                    ? `⚠ OVERFLOW by ${overBy}px`
                    : `✓ fits (${-overBy}px slack)`;
                if (overBy > 0) {

                }
            }
            // ── End post-layout audit ─────────────────────────────────────────────

            // Mark calculation complete BEFORE setting states
            isCalculatingRef.current = false;

            if (currentCalcRunId !== calcRunIdRef.current) return;


            layoutMeasureTokenRef.current += 1;

            if (!initialStateRef.current) {
                initialStateRef.current = {
                    states: JSON.parse(JSON.stringify(newCardStates)),
                    dataVersion: dataVersionRef.current,
                    // Overflowing widgets need scroll in their initial layout too —
                    // auto-revert restores this instead of force-disabling scroll.
                    scrollEnabled: overflowScroll,
                };
            }

            hasInitiallyLoadedRef.current = true;
            // Mark that this widget has completed its one-time entry load. After this,
            // any re-init (zoom re-estimate, resize) must stay silent — the entry
            // animation is an entrance effect, not something to replay on every zoom.
            hasEverLoadedRef.current = true;
            calcRetryRef.current = 0;

            // ── Overflow handling ─────────────────────────────────────────────────
            // Rule: if the FULL ticket count fits in the container → expand to all
            // tickets and play the entry animation.  Only when the full count truly
            // does not fit → load everything silently with scroll enabled.
            {
                const _overflowRemaining = normalizedCardsData.length - displayCardsData.length;
                if (_overflowRemaining > 0) {
                    isMeasuringRef.current = false;
                    isCalculatingRef.current = false;
                    isFirstCalculationRef.current = true; // bypass the 400ms calc throttle
                    // This calc captured initialStateRef with the PARTIAL (estimator)
                    // card count.  After expanding to the full count that snapshot's
                    // length never matches cardStates again, which silently disables
                    // the auto-revert-after-toggle feature.  Clear it so the follow-up
                    // full-count calc captures a correctly-sized snapshot.
                    initialStateRef.current = null;

                    if (!skipAnimation) {
                        // Initial load: expand to the FULL ticket count and replay the
                        // initial calc.  That replay measures all tickets in the DOM
                        // (authoritative — no prediction) and decides:
                        //   fits → entry animation plays once with every ticket;
                        //   overflows → overflowScroll=true routes it to the silent path.
                        // Widget is still hidden (isReady=false), so no flicker.

                        hasInitiallyLoadedRef.current = false;
                        preInitStep2ExpansionRef.current = true;
                        // Defer the full-count re-render too — committing hundreds of
                        // card nodes mid-animation of parallel widgets causes stutter.
                        //
                        // CRITICAL: do NOT gate this commit on calcRunId being current.
                        // We have already flipped hasInitiallyLoadedRef → false above, so
                        // shouldShowCards is false and the widget is hidden until the
                        // replay calc runs. The replay is ONLY triggered by this
                        // setMaxTicketsToRender call. If a newer calc bumped calcRunId
                        // while we waited (parallel animation, resize, measureAndApply,
                        // or a missing-refs retry that never reached this overflow block),
                        // a stale-guard return here would leave the widget permanently
                        // empty with no pending expand — the intermittent "large widget
                        // shows no tickets after config update" bug. Expanding to the full
                        // count is idempotent and always the correct terminal state for a
                        // measured-overflow widget; the maxTicketsToRender clamp effect and
                        // any in-flight full-reset reconcile everything else. So always
                        // commit.
                        _whenNoEntryAnimations(() => {
                            setMaxTicketsToRender(normalizedCardsData.length);
                        });
                        return;
                    }

                    // Silent recalc (resize/zoom): expand silently, keep scroll if overflowing.

                    noAnimationCalcRef.current = true;
                    step2ExpansionRef.current = true;
                    if (overflowScroll) setScrollEnabled(true);
                    setMaxTicketsToRender(normalizedCardsData.length);
                    return;
                }
            }

            // Tickets don't fit even at the current count → no animation, silent load.
            if (skipAnimation || overflowScroll) {
                // Silent resize recalculation — apply new layout instantly, no entry animation.
                // Reset interaction state so measureAndApply can fine-tune heights afterwards.
                hasUserInteractedRef.current = false;
                prevExpandedRef.current = new Set();
                // Preserve scroll when cards overflow the viewport; otherwise reset.
                setScrollEnabled(overflowScroll);
                entryAnimTimersRef.current.forEach(clearTimeout);
                entryAnimTimersRef.current = [];
                isEntryAnimatingRef.current = false;
                _entryAnimEnded(entryAnimCountedRef);
                setEntryAnimStage(0);
                setDisableBodyTransition(true);
                setDisableHeaderTransition(true);
                setCardStates(newCardStates);
                setIsReady(true);
                requestAnimationFrame(() => {
                    if (currentCalcRunId !== calcRunIdRef.current) return;
                    setDisableBodyTransition(false);
                    setDisableHeaderTransition(false);
                });
                return;
            }

            // When cards overflow the viewport, enable scroll immediately.
            if (overflowScroll) setScrollEnabled(true);

            // ── Entry animation: 3-stage sequence ──────────────────────────────
            // Stage 1: cards appear suddenly (no fade/slide), one by one top→bottom
            // Stage 2: headers expand one by one top→bottom
            // Stage 3: bodies expand one by one top→bottom
            const stage1States = newCardStates.map(s => ({
                ...s,
                isExpanded: false,
                visibleBodyHeight: 0,
                renderMode: "collapsed",
                lastCollapsedMode: false,
            }));

            // Set the animation flag synchronously before setCardStates so that any
            // ResizeObserver-triggered updateViewportHeight call that fires during the
            // re-render sees isEntryAnimatingRef.current = true and skips scheduling a
            // silent recalculation that would cancel this animation.
            isEntryAnimatingRef.current = true;
            _entryAnimStarted(entryAnimCountedRef);
            setCardStates(stage1States);

            requestAnimationFrame(() => {
                if (currentCalcRunId !== calcRunIdRef.current) return;

                const N = displayCardsData.length;
                const STAGGER_CARD = 60;
                const STAGGER_HEADER = 250;  // matches entry header transition (220ms) + buffer
                const STAGGER_BODY = 950;    // matches body transition (900ms) + buffer
                const HEADER_TRANSITION_MS = 220; // entry transition duration (must match CSS above)
                const BODY_TRANSITION_MS = 900;   // same as interactive body transition

                // Schedule a timer, guard stale runs, and track ID for cleanup
                const addTimer = (fn, delay) => {
                    const id = setTimeout(() => {
                        if (currentCalcRunId !== calcRunIdRef.current) return;
                        fn();
                    }, delay);
                    entryAnimTimersRef.current.push(id);
                };

                setEntryAnimStage(1);
                setIsReady(true);
                const _animOpenCount = newCardStates.filter(s => s.isExpanded).length;
                const _animMediumCount = newCardStates.filter(s => s.renderMode === "medium").length;
                const _animCollapsedCount = newCardStates.filter(s => !s.isExpanded && s.renderMode === "collapsed").length;

                // Stage 3: expand bodies one by one, then finish.
                // Only wait STAGGER_BODY after an expanded card — skip non-expanded instantly
                // so collapsed cards don't burn stagger time doing nothing.
                const startStage3 = () => {
                    setDisableBodyTransition(false);
                    setEntryAnimStage(3);
                    const revealBody = (i) => {
                        if (i >= N) {
                            addTimer(() => {
                                setEntryAnimStage(0);
                                isEntryAnimatingRef.current = false;
                                _entryAnimEnded(entryAnimCountedRef);
                                layoutMeasureTokenRef.current += 1;
                                setCardStates(prev => prev.map(s => ({ ...s })));
                            }, BODY_TRANSITION_MS + 200);
                            return;
                        }
                        if (newCardStates[i].isExpanded) {
                            setCardStates(prev => {
                                const next = prev.map(s => ({ ...s }));
                                next[i] = {
                                    ...next[i],
                                    isExpanded: true,
                                    renderMode: "open",
                                    visibleBodyHeight: newCardStates[i].visibleBodyHeight,
                                };
                                return next;
                            });
                            addTimer(() => revealBody(i + 1), STAGGER_BODY);
                        } else {
                            revealBody(i + 1); // no visual change — skip immediately
                        }
                    };
                    requestAnimationFrame(() => {
                        if (currentCalcRunId !== calcRunIdRef.current) return;
                        revealBody(0);
                    });
                };

                // Stage 2: expand headers one by one, then start stage 3.
                // Only wait STAGGER_HEADER after a card whose header actually changes —
                // skip collapsed→collapsed cards instantly.
                const startStage2 = () => {
                    setDisableHeaderTransition(false);
                    setEntryAnimStage(2);
                    const revealHeader = (i) => {
                        if (i >= N) {
                            addTimer(startStage3, HEADER_TRANSITION_MS + 80);
                            return;
                        }
                        const state = newCardStates[i];
                        const targetMode = state.renderMode === "open" ? "medium" : state.renderMode;
                        if (targetMode !== "collapsed") {
                            setCardStates(prev => {
                                const next = prev.map(s => ({ ...s }));
                                next[i] = {
                                    ...next[i],
                                    renderMode: targetMode,
                                    priorCompressedMode: state.priorCompressedMode,
                                    lastCollapsedMode: state.lastCollapsedMode,
                                };
                                return next;
                            });
                            addTimer(() => revealHeader(i + 1), STAGGER_HEADER);
                        } else {
                            revealHeader(i + 1); // no visual change — skip immediately
                        }
                    };
                    requestAnimationFrame(() => {
                        if (currentCalcRunId !== calcRunIdRef.current) return;
                        revealHeader(0);
                    });
                };

                const lastCardTime = Math.min(N - 1, 8) * STAGGER_CARD;
                // Per-widget phase offset: when several widgets animate in parallel,
                // their stage-2/3 timers use identical 250/950ms intervals and would
                // all fire setCardStates at the same instants, contending for the same
                // frames.  Offsetting each widget's start de-synchronizes the grids so
                // re-renders interleave across frames instead of colliding.
                addTimer(startStage2, lastCardTime + 80 + _getWidgetAnimStagger());
            });
        });
    };

    const toggleCard = (index) => {
        hasUserInteractedRef.current = true;
        setScrollEnabled(true);
        setDisableBodyTransition(false);
        setDisableHeaderTransition(false);
        if (revertAnimationTimerRef.current) {
            clearTimeout(revertAnimationTimerRef.current);
            revertAnimationTimerRef.current = null;
        }
        isRevertAnimatingRef.current = false;
        layoutMeasureTokenRef.current += 1;
        // Mark the toggle transition window (900ms flex-basis animation + margin) so
        // measureAndApply skips its forced-layout pass while the animation plays.
        isToggleAnimatingRef.current = true;
        if (toggleAnimTimerRef.current) clearTimeout(toggleAnimTimerRef.current);
        toggleAnimTimerRef.current = setTimeout(() => {
            isToggleAnimatingRef.current = false;
            toggleAnimTimerRef.current = null;
        }, 950);

        setCardStates((prevStates) => {
            // Clone only the 1-3 cards that actually change so unchanged cards keep
            // referential identity and memoized card components skip re-rendering.
            const newStates = [...prevStates];
            const currentMode = newStates[index]?.renderMode;

            if (currentMode === "open") {
                // Open ticket header clicked → collapse to orphan header only
                newStates[index] = {
                    ...newStates[index],
                    isExpanded: false,
                    isHalfOpen: false,
                    visibleBodyHeight: 0,
                    renderMode: "medium",
                    priorCompressedMode: "medium",
                };
            } else if (currentMode === "medium") {
                // Orphan header clicked → open this ticket only.
                // Match the height of any already-open card so all open cards are the same size.
                const alreadyOpen = newStates.find((s, i) => i !== index && s.isExpanded);
                // Match an already-open peer's height; otherwise use the CURRENT
                // dynamicOpenCardHeight. Do NOT fall back to newStates[index].fullHeight —
                // after a resize that's a stale pre-zoom height, so the toggled card would
                // open at the wrong size.
                const consistentHeight = alreadyOpen
                    ? alreadyOpen.visibleBodyHeight
                    : Math.floor(dynamicOpenCardHeight);
                newStates[index] = {
                    ...newStates[index],
                    isExpanded: true,
                    isHalfOpen: false,
                    visibleBodyHeight: consistentHeight,
                    renderMode: "open",
                };
            } else {
                // Stack (collapsed) clicked → convert clicked + before + after to orphan headers.
                // Only pull adjacent cards in when they are STILL inside the stack
                // (renderMode "collapsed"). Cards already shown as open headers stay untouched.
                const toConvert = new Set([index]);
                if (index > 0 && newStates[index - 1]?.renderMode === "collapsed") toConvert.add(index - 1);
                if (index < newStates.length - 1 && newStates[index + 1]?.renderMode === "collapsed") toConvert.add(index + 1);

                toConvert.forEach((i) => {
                    newStates[i] = {
                        ...newStates[i],
                        isExpanded: false,
                        isHalfOpen: false,
                        visibleBodyHeight: 0,
                        renderMode: "medium",
                        priorCompressedMode: "medium",
                    };
                });
            }

            return newStates;
        });

        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
        }

        if (autoRevertDurationinSec > 0) {
            const currentVersion = dataVersionRef.current;
            autoRevertTimerRef.current = setTimeout(() => {
                if (initialStateRef.current &&
                    initialStateRef.current.dataVersion === currentVersion &&
                    initialStateRef.current.states.length === cardStates.length) {
                    // Refresh open cards to the CURRENT dynamicOpenCardHeight — the snapshot
                    // was captured at an earlier zoom (a silent unchanged-count resize never
                    // re-captures it), so reverting verbatim would reapply a stale height.
                    const _openH = Math.floor(dynamicOpenCardHeight);
                    const restoredState = JSON.parse(JSON.stringify(initialStateRef.current.states)).map((s) =>
                        (s.renderMode === "open" || s.isExpanded)
                            ? { ...s, fullHeight: _openH, visibleBodyHeight: _openH }
                            : s
                    );
                    // Reset interaction flags so measureAndApply can re-run after the
                    // revert animation settles. Allow transitions so the reset animates smoothly.
                    hasUserInteractedRef.current = false;
                    prevExpandedRef.current = new Set();
                    setScrollEnabled(Boolean(initialStateRef.current.scrollEnabled));
                    isRevertAnimatingRef.current = true;
                    setDisableBodyTransition(false);
                    setDisableHeaderTransition(false);
                    layoutMeasureTokenRef.current += 1;
                    setCardStates(restoredState);
                    revertAnimationTimerRef.current = setTimeout(() => {
                        isRevertAnimatingRef.current = false;
                        revertAnimationTimerRef.current = null;
                        setDisableBodyTransition(true);
                    }, 1100);
                }
                autoRevertTimerRef.current = null;
            }, autoRevertDurationinSec * 1000);
        }
    };

    const handleImageError = useCallback((cardId) => {
        setImageErrors((prev) => ({ ...prev, [cardId]: true }));
    }, []);

    // Stable wrapper so memoized cards don't re-render just because toggleCard
    // is a fresh closure each render.
    toggleCardLatestRef.current = toggleCard;

    const shouldShowCards = isReady || hasInitiallyLoadedRef.current;

    return (
        <div ref={parentRef} style={{ height: "calc(100% - 0.05em)", width: "100%", backgroundColor: isDarkTheme ? "#1a1a1a" : "#f0f0f0" }}>
            <div
                className={isDarkTheme ? "slim-scroll-dark" : "slim-scroll"}
                ref={scrollContainerRef}
                style={{
                    width: "100%",
                    height: "100%",
                    overflowY: scrollEnabled ? "auto" : "hidden",
                    overflowX: "hidden",
                    // Reserve the scrollbar gutter once scrolling is enabled: during the
                    // 900ms open/close animation the content height crosses the overflow
                    // threshold, and without a stable gutter the scrollbar pops in/out,
                    // changing the container width and forcing a full relayout of every
                    // card mid-animation (visible as lag in large ticket widgets only —
                    // small widgets never overflow, which is why they stay smooth).
                    scrollbarGutter: scrollEnabled ? "stable" : "auto",
                    opacity: shouldShowCards ? 1 : 0,
                    visibility: shouldShowCards ? "visible" : "hidden",
                    transition: "none",
                    backgroundColor: isDarkTheme ? "#1a1a1a" : "#f0f0f0",
                }}
            >
                <div ref={gridRef} className="main-card-body" style={{
                    margin: "8px", overflow: "visible",
                }}>
                    {displayCardsData.map((card, index) => (
                        <TicketCard
                            key={card.id}
                            card={card}
                            index={index}
                            cardState={cardStates[index] || EMPTY_CARD_STATE}
                            isLastCard={index === displayCardsData.length - 1}
                            isDarkTheme={isDarkTheme}
                            themeStyles={themeStyles}
                            compactStatusStyle={compactStatusStyle}
                            entryAnimStage={entryAnimStage}
                            isReady={isReady}
                            disableBodyTransition={disableBodyTransition}
                            disableHeaderTransition={disableHeaderTransition}
                            isOpenTicketPlacementTop={isOpenTicketPlacementTop}
                            headerLabelMode={headerLabelModesRef.current[card.id] || 'full'}
                            imageError={Boolean(imageErrors[card.id])}
                            onToggle={onToggleCard}
                            onImageError={handleImageError}
                            hasUserInteractedRef={hasUserInteractedRef}
                            isEntryAnimatingRef={isEntryAnimatingRef}
                            headerSpanRefs={headerSpanRefs}
                            cardRefs={cardRefs}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
